import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { shouldUseSourceService } from './shared/helpers/helpers';
import { handleFirebasePermissionError, isFirebasePermissionError } from './shared/helpers/firebase-permission-error.handler';
import { isMissingRouteParam } from './shared/helpers/route-params.helper';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpUrlEncodingCodec, HttpResponse
} from '@angular/common/http';
import { isLocalStorageAvailable } from './shared/helpers/helpers';
// Interface for getDataSources parameters
export interface DataSourcesParams {
  status?: string;
  parallelism?: number;
  beta?: boolean;
  templates?: boolean;
  dashboard?: boolean;
  datasourceIds?: number[];
  includeSettings?: boolean;
  type?: string;
  displayName?: string;
  logoUrl?: string;
  loginType?: string;
  displayCategory?: number;
  cdkSource?: boolean;
  datasourceCategory?: string;
  types?: string[];
}

// Interface for getUrl parameters
export interface GetUrlOptions {
  isLegacy: boolean | null;
  isCdk: boolean | null;
  legacySourceId: number;
  purposeType: string;
  mode?: string;
  migrated?: boolean;
}

import { CacheService } from './cache.service';
import { SnackBarService } from './snack-bar.service';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import {
  CreateSourceRequest,
  CheckoutData,
  Company,
  Datasource,
  Datawarehouse,
  Notification,
  NotificationResult,
  Source,
  SourceAccount,
  SourceUpdateData,
  StepData,
  StepProceed,
  ReportCategory,
  IntegrationConfigStatus,
  Token,
  UserDetails,
  UserPassword,
  UserForgotPassword,
  Warehouse,
  WarehouseAccount,
  PaginationInfo,
  Invoices,
  CurrentCycleStats,
  Table,
  EnabledWarehouse,
  ConfiguredSource,
  TableNames,
  SourceLimit,
  RunningSource,
  SourceTransactionLog,
  Brand,
  DatasourceResponse
} from './app.model';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CommonDialogComponent } from './common-dialog/common-dialog.component';
import { Observable, Subject, Subscription, of, interval, BehaviorSubject, throwError, from, defer, timer } from 'rxjs';
import * as moment from 'moment-timezone';
import { AngularFirestore } from '@angular/fire/compat/firestore';
// import {MixpanelService} from './services/mixpanel.service';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import { catchError, retry, retryWhen, concatMap, delay, skipWhile, switchMap, take, map as rxjsMap, first as rxjsFirst } from 'rxjs/operators';
import { size, get, isEmpty, includes, forEach, map } from 'lodash-es';
import { NS_Enums, oldPricingRoute } from './shared/namespaces';
import { SourceGraphQLService } from './graphql/source.service';
import { PosthogService } from './services/posthog/posthog.service';

const baseWord = 'u';
const uncommonBaseWord = '';
const secondsCounter = interval(1000);
const ADMIN_SOURCE_OPTIONS_PAGE_SIZE = 200;
interface HttpError {
  status?: number;
  error?: { message?: string };
  message?: string;
}

/** Admin integration search response. */
export interface AdminIntegrationPage {
  integrations: RunningSource[];
  total: number;
  totalIsExact: boolean;
}

/** Filters supported by Admin integration search APIs. */
export interface AdminIntegrationFilterCriteria {
  company: string | number;
  email: string | number;
  sourceType: string | number;
  sourceId: string | number;
  sourceName: string | number;
  sourceStatus: string | number;
}

/** Source option returned for Admin integration filters. */
export interface AdminIntegrationSourceOption {
  sourceId: number;
  name?: string;
  sourceName?: string;
  userid?: number;
  userId?: number;
}

/** Metadata used to select legacy or source-service synchronization. */
export interface SourcePlatformMetadata {
  isLegacy?: boolean;
  migrated?: boolean;
}

export type StartSourceRouting =
  | 'admin-new'
  | 'admin-legacy'
  | 'auto';

@Injectable()
export class AppService  {
  /** verifyfiretoken tries before an outage is declared, and the pause (x attempt number) between tries. */
  static readonly VERIFY_TOKEN_ATTEMPTS = 3;
  static readonly VERIFY_TOKEN_RETRY_DELAY_MS = 1000;
  /** Upper bound on how long `getRefreshToken` may block an API call. */
  static readonly REFRESH_TOKEN_TIMEOUT_MS = 8000;
  /** How often a pending token refresh checks whether Daton sign-in has stored its auth key. */
  static readonly AUTH_KEY_POLL_MS = 250;

  private sourceEvent = new Subject<Source>();
  private warehouseEvent = new Subject<Warehouse>();
  private warehouseBannerEvent = new Subject<boolean>();
  private closeDropDown = new Subject<boolean>();
  private noConnection = new Subject<boolean>();
  private trialUpdate = new Subject<string>();
  private userLoginEvent = new Subject<boolean>();
  private currentUserRoute = new Subject<string>();
  private currentUserRole = new Subject<any>();
  private signInEvent = new Subject<string>();
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  private params = new HttpParams();
  private appUrl = environment.apiUrl;
  private vmUrl = environment.vmUrl
  private userServiceUrl = environment.userServiceApiUrl
  private codec = new HttpUrlEncodingCodec();
  private stepChange = new Subject<StepProceed>();
  private pageChangeEvent = new Subject<PaginationInfo>();
  private cbInstance;
  private chargebeeInitTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private notificationEvent = new Subject<Notification[]>();
  private ifEnterprisePlan = new Subject<boolean>();
  private userDetails: UserDetails | null = null;
  
  /**
   * BehaviorSubject for userDetails to avoid unnecessary API calls
   * Components can subscribe to userDetails$ to get real-time updates
   * 
   * Usage in components:
   * 
   * 1. Subscribe to userDetails$ observable:
   *    this.appService.userDetails$.subscribe(user => {
   *      console.log('User details:', user);
   *    });
   * 
   * 2. Get current value synchronously:
   *    const user = this.appService.getUserDetailsValue();
   * 
   * 3. Update userDetails:
   *    this.appService.setUserDetails(updatedUser);
   * 
   * 4. Force refresh from API:
   *    this.appService.getUser(3, userId, true);
   * 
   * 5. Clear userDetails (on logout):
   *    this.appService.clearUserDetails();
   */
  private _userDetails$ = new BehaviorSubject<UserDetails | null>(null);
  public readonly userDetails$ = this._userDetails$.asObservable()
    .pipe(skipWhile(data => data === undefined || data === null));
  private pendingUserRequest: Promise<UserDetails> | null = null;
  private dashboardPermissionGrantedEvent = new Subject();
  private currentSubscription: any;
  private _currentSubscription$ = new BehaviorSubject <any> (null);
  subscriptionPlan: any = {};
  timeFormat = 'D-MMM-YY, hh:mm:ss A z';
  titleTimeFormat = 'D-MMM-YY';
  over_lapTime = 1500000;
  public viewedNotificationsAt: number = isLocalStorageAvailable() ? Number(localStorage.getItem('viewedNotificationsAt')) : 0;  // 0 refers to date in milliseconds for January 1, 1970, UTC, localStorage unavailable; treat all notifications as unseen
  maintenanceMode$ = new BehaviorSubject<any>(false);

  authValidationInProgress: boolean = false;
  private apiUrlSourceService = environment.apiUrlSourceService;
  private adminIntegrationsUrl =
    `${this.apiUrlSourceService.replace(/\/$/, '')}/admin/integrations`;
  private studioApiUrl = environment.studioApiUrl;
  globalConnectorDataSource:any[] = []
  public readonly currentSubscription$ = this._currentSubscription$.asObservable()
  .pipe(skipWhile(data => data === undefined || data === null));

  initAndStoreCB() {
    const maxRetries = 5;
    const retryDelay = 200;
    const maxWaitTime = 5000;

    this.cancelChargebeeInit();

    const scheduleRetry = (retryCount: number, startTime: number) => {
      this.chargebeeInitTimeoutId = setTimeout(
        () => attemptInit(retryCount + 1, startTime),
        retryDelay
      );
    };

    const attemptInit = (retryCount = 0, startTime = Date.now()) => {
      this.chargebeeInitTimeoutId = null;
      try {
        if (!window['Chargebee']) {
          if (retryCount >= maxRetries - 1 || Date.now() - startTime > maxWaitTime) {
            throw new Error('Chargebee SDK not loaded after max wait time');
          }

          // Chargebee script is still loading, retry after delay
          scheduleRetry(retryCount, startTime);
          return;
        }

        // Verify Chargebee has the init method
        if (typeof window['Chargebee'].init !== 'function') {
          throw new Error('Chargebee.init is not a function');
        }

        this.cbInstance = window['Chargebee'].init({
          site: environment.chargeBeeSite,
        });

        if (!this.cbInstance) {
          throw new Error('Chargebee init returned null/undefined');
        }
      } catch (error) {
        console.error(`Chargebee initialization failed (attempt ${retryCount + 1}/${maxRetries}):`, error);

        if (retryCount < maxRetries - 1 && Date.now() - startTime < maxWaitTime) {
          scheduleRetry(retryCount, startTime);
        } else {
          console.error('Chargebee initialization failed after all retries or timeout. App will continue without Chargebee support.');
          this.cbInstance = null;
        }
      }
    };

    attemptInit();
  }

  /** Clears any pending Chargebee init retry timer (used by tests to avoid leaked loops). */
  cancelChargebeeInit() {
    if (this.chargebeeInitTimeoutId != null) {
      clearTimeout(this.chargebeeInitTimeoutId);
      this.chargebeeInitTimeoutId = null;
    }
  }

  canActivateChild() {
    if (!navigator.onLine) {
      this.router
        .navigate(['/no-internet'], {
          state: { data: { redirectUrl: window.location.href } },
        })
        .then();
    }
    return true;
  }

  canActivate(): boolean {
    return false;
  }

  getTimeFormat(): string {
    return this.timeFormat;
  }

  getCB(): any {
    return this.cbInstance || null;
  }

  /**
   * Null-safe Chargebee checkout opener shared by billing/account call sites.
   * Invokes onUnavailable when the SDK is missing or openCheckout throws.
   */
  openCheckoutSafely(options: any, onUnavailable?: () => void): void {
    const cbInstance = this.getCB();
    if (!cbInstance) {
      console.error('Chargebee is not initialized. Cannot open payment checkout.');
      onUnavailable?.();
      return;
    }

    try {
      cbInstance.openCheckout(options);
    } catch (error) {
      console.error('Error opening Chargebee checkout:', error);
      onUnavailable?.();
    }
  }

  getBaseWord(): string {
    return baseWord;
  }

  getUncommonBaseWord(): string {
    return uncommonBaseWord;
  }

  private setAuthHeader(): HttpHeaders {
    return this.headers.set(
      'Authorization',
      'Bearer ' + this.cacheService.getObject<Token>('saras_auth_key').token
    );

  }


  // private createAuthHeader(): HttpHeaders {
  //   return this.headers.set(
  //     "Authorization",
  //     "Bearer " + this.cacheService.getObject<Token>("saras_auth_key").token
  //   );
  // }

  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private router: Router,
    private snackBarService: SnackBarService,
    //  private mixpanel: MixpanelService,
    private dialog: MatDialog,
    private afAuth: AngularFireAuth,
    private sourceGraphQLService: SourceGraphQLService,
    private posthogService: PosthogService,
    private snackBar: MatSnackBar
  ) {
  }
  private subscriptionBannerVisible = new BehaviorSubject<boolean>(false);
  subscriptionBannerVisible$ = this.subscriptionBannerVisible.asObservable();

  setSubscriptionBannerVisible(isVisible: boolean) {
    this.subscriptionBannerVisible.next(isVisible);
  }

  initializeService() {
    const saras_auth_key = this.cacheService.getObject<Token>('saras_auth_key');
    if (saras_auth_key) {
      const user_details = this.cacheService.getObject<any>('user_details');
      if (!user_details) {
        this.getUser().then((user) => {
          this.userDetails = user;
          this._userDetails$.next(user);
          this.cacheService.putObject('user_details', user, true);
        });
      } else {
        // If user details exist in cache, populate the BehaviorSubject
        this.userDetails = user_details;
        this._userDetails$.next(user_details);
      }
    }
  }

  get userData() {
    return this.cacheService.getObject<any>('user_details');
  }

  /**
   * Get the current userDetails value synchronously
   */
  getUserDetailsValue(): UserDetails | null {
    return this._userDetails$.getValue();
  }

  /**
   * Set userDetails and update the BehaviorSubject
   */
  setUserDetails(user: UserDetails): void {
    this.userDetails = user;
    this._userDetails$.next(user);
    this.cacheService.putObject('user_details', user, true);
    this.posthogService.identify(user);
  }

  /**
   * Clear userDetails from BehaviorSubject and cache
   * Should be called on logout
   */
  clearUserDetails(): void {
    this.userDetails = null;
    this._userDetails$.next(null);
    this.pendingUserRequest = null;
    this.cacheService.remove('user_details');
    this.posthogService.reset();
  }

  openErrorDialog(error: any, redirect: boolean, mixPanelData: any = {}) {
    let errorMessage;
    let targetUrl;
    let statusCode;
    let documentURL:string;
    let documentName:string;
    try {
      targetUrl = error?.url;
      statusCode = error?.status;
      console.log(`TARGET URL - ${targetUrl}`)
      errorMessage = error.error.errors[0].message;
    } catch (e) {
      errorMessage = error.statusText;
    }
    if (errorMessage === 'Forbidden' || errorMessage === 'Token is not valid') {
      errorMessage = 'Token is not valid. logging out..';
    }
    if (errorMessage === 'We couldn\'t access your Bigquery account. To resolve the error, please add the following permissions in your GCP IAM tab to 210514.iam.gserviceaccount.com and try again. 1.BigQuery Job User, 2.BigQuery Data Owner, For more information, refer to documentation : https://docs.sarasanalytics.com/integrations/destinations/bigquery/setup-google-bigquery' ){
      errorMessage = errorMessage.split('documentation : ')
      documentURL = errorMessage[1];
      errorMessage = errorMessage[0];
      documentName = 'Documentation'

      }
    if(errorMessage === 'AMAZONREDSHIFT: Read-Write permission issue'){
      errorMessage = 'We couldn\'t access your warehouse. Please check if you have read & write permissions to the schema selected. For more information, refer to'
      documentName = 'Documentation'
      documentURL = 'https://docs.sarasanalytics.com/integrations/destinations/amazon-redshift';
    }

    if(errorMessage === 'SNOWFLAKE: Read-Write permission issue'){
      errorMessage = 'We couldn\'t access your warehouse. Please check if you have read & write permissions to the schema selected. For more information, refer to'
      documentName = 'Documentation'
      documentURL = 'https://docs.sarasanalytics.com/integrations/destinations/snowflake';
    }

    if(errorMessage === 'Oracle: Read-Write permission issue'){
      errorMessage = 'We couldn\'t access your warehouse. Please check if you have read & write permissions to the schema selected. For more information, refer to'
      documentName = 'Documentation'
      documentURL = 'https://docs.sarasanalytics.com/integrations/destinations/oracle-autonomous-data-warehouse';
    }

    if(errorMessage === 'amazon-s3: Read-Write permission issue'){
      errorMessage = 'We couldn\'t access your warehouse. Please check if you have read & write permissions to the schema selected. For more information, refer to'
      documentName = 'Documentation'
      documentURL = 'https://docs.sarasanalytics.com/integrations/destinations/amazon-s3';
    }
    if(error === 'Contact Support' || error === 'Subscribe for payment information'){
      errorMessage = error;
    }
    if(errorMessage == 'OK') {
      errorMessage = 'Oops! Something went wrong'
    }

    if(includes(targetUrl, 'verifyfiretoken') && statusCode === 0) {
      errorMessage = 'Unable to login. Please try again later.'
    }

    if(errorMessage == 'Unknown Error') {
      errorMessage = 'Server Not Responding.'
    }

    if (environment.apiUrl === 'https://webapi.sarasanalytics.com/' ) {
      window['dataLayer'] = window['dataLayer'] || [];
      window['dataLayer'].push({
        event: mixPanelData,
        eventCategory: '',
        eventAction: 'Failure',
        eventLabel: error,
      });
    }

    if ((statusCode !== 0 && statusCode < 500) || (statusCode === 0 && targetUrl.includes('verifyfiretoken'))) {
      const dialogRef = this.dialog.open(CommonDialogComponent, {
        data: {
          title: 'Alert!',
          content: errorMessage,
          documentURL: documentURL,
          documentName: documentName,
          image: 'error',
          buttons: [
            {
              title: 'OK',
              meta: '/integrations-list',
              type: 'no-change',
              class: 'confirm-dialog-later',
            },
          ],
        },
        disableClose: true,
      });
  
  
  
      dialogRef.afterClosed().subscribe((result) => {
        if (errorMessage === 'Token is not valid. logging out..') {
          this.cacheService.clear();
          this.clearUserDetails();
          this.getUserLoginEvent().next(false);
          if (redirect) {
            this.router.navigate(['/']).then();
          }
        }
        if(errorMessage === 'Subscribe for payment information'){
          this.router.navigate([oldPricingRoute]);
        }
        if(errorMessage === 'Not Allowed') {
          this.router.navigate(['/']);
        }
      });
    }
  }

  handleError(
    error: any,
    redirect = true,
    mixPanelData: any = {}
  ): Promise<any> {
    if (isFirebasePermissionError(error)) {
      handleFirebasePermissionError(error, this.snackBar, this.cacheService.hasValidAuthKey());
      return Promise.reject(error.message || error);
    }
    this.openErrorDialog(error, redirect, mixPanelData);
    return Promise.reject(error.message || error);
  }

  handleTempError(error: any): Promise<any> {
    this.router.navigate(['/' + baseWord + '/datasources-listing']);
    return Promise.resolve(null);
  }

  handleErrorDontRedirect(error: any): Promise<any> {
    if (isFirebasePermissionError(error)) {
      handleFirebasePermissionError(error, this.snackBar, this.cacheService.hasValidAuthKey());
      return Promise.reject(error);
    }
    this.openErrorDialog(error, false);
    return Promise.reject(error);
  }

  routeToLogin() {
    if (!this.router.url.includes('/login')) {
      this.router.navigate(['/login'], {
        queryParams: { routeTo: this.router.url },
      });
    }
  }

  async subscriptionColumnMapping() {

    await this.getRefreshToken();

    return this.http
      .get<any>(this.appUrl + 'subscriptions/header')
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async userIntegrationLimit(
    userId: number,
    datasourceId: number,
    notify = true,
    actionOn = '',
    actionType = ''
  ): Promise<SourceLimit> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const generateUserNotification = notify ? 'true' : 'false';
    const params = this.params
      .append('notify', generateUserNotification)
      .append('actionOn', actionOn)
      .append('actionType', actionType);
    return this.http
      .get<SourceLimit>(
        this.appUrl +
          'source/checksourcelimit/' +
          userId.toString() +
          '/' +
          datasourceId.toString(),
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async setWarehouse(warehouseId, sourceId, userId, type) {

    await this.getRefreshToken();

    const headers = this.setAuthHeader();

    return this.http
      .get<SourceLimit>(
        this.appUrl +
          'source/' +
          userId.toString() + '/' + warehouseId.toString() + '/'+ type.toString()  + '/set-warehouse/' +
           + sourceId.toString(),
        { headers: headers}
      )
      .toPromise()
      .catch((err) => this.handleError(err));


  }

  columnMapping(): any {
    return {
      name: 'PLAN TYPE',
      rowsInMillion: 'Rows in Million',
      noOfIntegrations: 'Number of Integrations',
      onBoarding: 'On Boarding',
      customerSuccessManager: 'Customer Success Manager',
      chatSupport: 'Chat Support',
      prioritySupport: 'Priority Support',
      reportDiscounting: 'Discounting on Reporting and MLSc',
      analystDiscounting: 'Discount or BI Analyst Service',
      // sla: 'SLA',
      price: 'Pricing',
    };
  }

  columnMappingMyAccount(): any {
    return {
      rowsInMillion: 'Rows',
      noOfIntegrations: 'Integrations',
      onBoarding: 'On Boarding',
      customerSuccessManager: 'Customer Success Manager',
      chatSupport: 'Chat Support',
      prioritySupport: 'Priority Support',
    };
  }

  // requiredSubscripitonColumns() {
  //   return ['name', 'rowsInMillion', 'noOfIntegrations', 'onBoarding',
  //     'customerSuccessManager', 'chatSupport', 'prioritySupport', 'reportDiscounting', 'analystDiscounting', 'price'];
  // }

  getPageChangeEvent(): Subject<PaginationInfo> {
    return this.pageChangeEvent;
  }

  /**
 * Handles errors from step data API calls.
 * Displays user-friendly error messages via snackbar for HTTP errors.
 * 
 * @param error - The error object from the HTTP request
 * @returns Promise resolving to the error object for further handling
 */
  stepDataError(error: HttpError): Promise<HttpError> {
    // AppService.closeModal();
    if (error?.status) {
      // Handle other HTTP errors
      this.snackBarService.open(this.getErrorMessage(error), 5000);
    }
    return Promise.resolve(error);
  }
  private getErrorMessage(error: HttpError): string {
    return error?.error?.message 
      || error?.message 
      || `Server error (${error.status}). Please try again.`;
  }

  getNotifications(): Subject<Notification[]> {
    return this.notificationEvent;
  }

  getDashboardPermissionGrantedEvent(): Subject<unknown>{
    return this.dashboardPermissionGrantedEvent;
  }

  emitDashboardPermissionGrantedEvent(){
    this.dashboardPermissionGrantedEvent.next();
  }

  getSourceEvent(): Subject<Source> {
    return this.sourceEvent;
  }

  getStepChangeEvent(): Subject<StepProceed> {
    return this.stepChange;
  }

  getWarehouseEvent(): Subject<Warehouse> {
    return this.warehouseEvent;
  }

  getWarehouseBannerEvent(): Subject<boolean> {
    return this.warehouseBannerEvent;
  }

  closeSourceDetinationDropDown(): Subject<boolean> {
    return this.closeDropDown;
  }

  getNoConnectionEvent(): Subject<boolean> {
    return this.noConnection;
  }

  getTrialPlanUpdate(): Subject<string> {
    return this.trialUpdate;
  }

  getUserLoginEvent(): Subject<boolean> {
    return this.userLoginEvent;
  }

  getCurrentUserRouteEvent(): Subject<string> {
    return this.currentUserRoute;
  }

  getcurrentUserRole(): Subject<any> {
    return this.currentUserRole;
  }

  async resendVerificationLink(userDetails: UserDetails): Promise<any>{

    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .put<string>(this.appUrl + 'user/resend-email', userDetails, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err, false));

  }

  // Emails are ASCII-only; toLowerCase() is locale-safe here (avoids Turkish-i hazard of toLocaleLowerCase).
  private normalizeEmail(email: string): string;
  private normalizeEmail(email?: string): string | undefined;
  private normalizeEmail(email?: string): string | undefined {
    return email?.trim().toLowerCase();
  }

  signUp(userSignUpInfo: UserDetails): Promise<string> {
    const normalizedInfo = { ...userSignUpInfo, email: this.normalizeEmail(userSignUpInfo.email) };
    const mixpanelData = {
      eventName: 'signup',
      data: {
        email: normalizedInfo.email,
      },
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'skip': 'true'
    });
    const params = this.params;
    return this.http
      .put<string>(this.appUrl + 'user', normalizedInfo, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err, false, mixpanelData));
  }

  signIn(email: string, password: string, eventName = ''): Promise<Token> {
    const normalizedEmail = this.normalizeEmail(email);
    const headers = this.headers;
    const params = this.params.set('email', normalizedEmail).set('password', password);
    return this.http
      .post<Token>(
        this.appUrl + 'user/login',
        {},
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) =>
        this.handleError(err, false, {
          eventName: eventName,
          data: { email: normalizedEmail },
        })
      );
  }

  getCompanies(): Promise<Company[]> {
    return this.http
      .get<Company[]>(this.appUrl + 'company/all')
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async onSaveChanges(user: UserDetails): Promise<UserDetails> {

    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .put<string>(this.appUrl + 'user/change-info', user, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err, false));

  }


  async invoicesZip(userId: number,selectedInvoices:string): Promise<any> {

    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params.append('selectedInvoices', selectedInvoices);
    try {
      return this.http
        .get(this.appUrl + 'user/' + userId + '/DownloadUrls', {
          headers: headers,
          params: params,
          responseType: 'arraybuffer'
        })
        .toPromise();
    } catch (err) {
      return await this.handleError(err);
    }
  }
  /**
   * Gets invoice pdf
   * @param userId
   * @param invoiceid
   * @returns invoice pdf
   * @description
   */
  async getInvoicePdf(userId: number, invoiceid: string): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'user/' + userId + '/invoicePdf/' + invoiceid, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }
  /**
   * Gets invoicesfor first time
   * @param userId
   * @returns invoicesfor first time
   */
  async getInvoicesforFirstTime(userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'user/' + userId + '/getInvoices', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }
  /**
   * Gets all invoices
   * @param userId
   * @returns all invoices
   */
  async getAllInvoices(userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'user/' + userId + '/allInvoiceDetailsBilling', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }
  /**
   * Gets invoices offset
   * @param userId
   * @param param1
   * @param param2
   * @returns invoices offset
   */
  async getInvoicesOffset(
    userId: number,
    param1: string,
    param2: string
  ): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(
        this.appUrl +
          'user/' +
          userId +
          '/getInvoices/' +
          param1 +
          '/' +
          param2,
        {
          headers: headers,
          params: params,
        }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

   verifyEmail(id: number, code: string): Promise<any> {

    // await this.getRefreshToken();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'skip': 'true'
    });
    const params = this.params;
    return this.http
      .get<string>(this.appUrl + 'user/' + id + '/validate/' + code, {
        headers: headers,
        params: params,
        observe: 'response',
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

   saveDataToFirebaseDatabase(userId: number): Promise<any> {

  //  await this.getRefreshToken();

    const headers = this.headers;
    const params = this.params;
    return this.http
      .get<string>(this.appUrl + 'user/' + userId + '/saveToFirebase', {
        headers: headers,
        params: params,
        observe: 'response',
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async associateBrandWithUser(brandId: number, userId: number, attemptCount = 3): Promise<Brand> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
   // const userId = this.cacheService.getObject<Token>(NS_Enums.cacheKeysEnum.saras_auth_key).userId;
    return this.http
      .put<Brand>(this.appUrl + 'brand/associate-user' + '/' + brandId + '/' + userId,{}, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => {

        //if(attemptCount < 0 ) {
          return this.handleError(err)
       // }

        //this.associateBrandWithUser(brandId, userId, --attemptCount)

      });
  }

  async deleteBrand(brandId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    const userId = this.cacheService.getObject<Token>(NS_Enums.cacheKeysEnum.saras_auth_key).userId;
    return this.http
      .delete<any>(this.appUrl + 'brand/' + brandId + '/' + userId, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => {

       // if(attemptCount < 0 ) {
          return this.handleError(err)
       // }

       // this.addBrand(brandName, --attemptCount)

      });
  }

  async addBrand(brandName: string, attemptCount = 3): Promise<Brand> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    const userId = this.cacheService.getObject<Token>(NS_Enums.cacheKeysEnum.saras_auth_key).userId;
    return this.http
      .post<Brand>(this.appUrl + 'brand/' + userId + '/' + brandName,{}, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => {

       // if(attemptCount < 0 ) {
          return this.handleError(err)
       // }

       // this.addBrand(brandName, --attemptCount)

      });
  }

  async getBrands(attemptCount = 3, specificUserid = null): Promise<Brand[]> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    const userId = specificUserid == null ? this.cacheService.getObject<Token>(NS_Enums.cacheKeysEnum.saras_auth_key).userId : specificUserid;
    return this.http
      .get<Brand[]>(this.appUrl + 'brand/' + userId, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => {

        if(attemptCount < 0 ) {
          return this.handleError(err)
        }

        this.getBrands(--attemptCount)

      });
  }

  async getUser(attemptCount = 1, user?:number, forceRefresh = false): Promise<UserDetails> {
    // Check if userDetails already exists in BehaviorSubject (unless forceRefresh is true)
    const cachedUserDetails = this._userDetails$.getValue();
    if (cachedUserDetails && !forceRefresh) {
      return Promise.resolve(cachedUserDetails);
    }

    // If there's already a pending request, return it to share the same API call
    if (this.pendingUserRequest && !forceRefresh) {
      console.log('User details: Returning pending request (shared)');
      return this.pendingUserRequest;
    }

    // Create a new request and store it
    console.log('User details: Fetching from API');
    const requestPromise = this.fetchUserDetails(attemptCount, user);
    this.pendingUserRequest = requestPromise;

    try {
      const userDetails = await requestPromise;
      return userDetails;
    } catch (error) {
      throw error;
    } finally {
      // Only clear if this is still the current pending request
      if (this.pendingUserRequest === requestPromise) {
        this.pendingUserRequest = null;
      }
    }
  }

  private async fetchUserDetails(attemptCount: number, user?: number): Promise<UserDetails> {
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    const userId = user || this.cacheService.getObject<Token>(NS_Enums.cacheKeysEnum.saras_auth_key)?.userId;

    try {
      // Try to fetch the user details
      let userDetails = await this.http.get<UserDetails>(`${this.appUrl}user/${userId}`, {
        headers: headers,
        params: params,
      }).toPromise();
      userDetails = {...userDetails, fetchedFromCache: true}
      // Update the BehaviorSubject and cache
      this.setUserDetails(userDetails);
      
      return userDetails;
      
    } catch (err) {
      // Check for server errors or network issues
      if (err.status >= 500 || err.status === 0) {
        if (attemptCount <= 1) {
          // Set maintenance mode if all retry attempts fail
          this.maintenanceMode$.next(true);
          this.router.navigate(['/logout']);
          return Promise.reject(err);  // Reject the promise to pass the error up
        }
      }

      // Retry logic if attempts remain
      if (attemptCount > 1) {
        // Clear pending request before retry
        this.pendingUserRequest = null;
        return this.getUser(--attemptCount, user, false);
      } else {
        // If no attempts left, handle the error
        this.router.navigate(['/logout']);
        return this.handleError(err);
      }
    }
  }

  getUserRoles(){
    const user: any = this.cacheService.getObject(NS_Enums.cacheKeysEnum.user_details);

    try{
      return this.http.get<UserDetails>(`${this.userServiceUrl}/user/userRolesByUserId`, {
        params: {
          userId: user?.userId,
          productType: 'DATON'
        }
      }).pipe(
        switchMap((res: any)=>{
          return res.data || [];
        })
      )
    }catch(e){
      return of([])
    }
  }

  getUserAsObservable(attemptCount = 3): Observable<UserDetails | any> {

    return new Observable((observer)=>{
      this.getRefreshTokenAsObservable().pipe(take(1), catchError(err=>{
        this.handleError(err);
        return throwError(err);
      })).subscribe(()=>{
        const headers = this.setAuthHeader();
        const params = this.params;
        const userId = this.cacheService.getObject<Token>('saras_auth_key').userId;
        this.http
          .get<UserDetails>(this.appUrl + 'user/' + userId, {
            headers: headers,
            params: params,
          }).pipe(retry(attemptCount), catchError(err=>{
            this.handleError(err);
            return throwError(err);
          })).subscribe((user)=>{
            observer.next(user);
          }, err =>{
            observer.error(err);
          })
      })
    })
  }

  underUserAddingProcess = false;
  async addNewUser(user: UserDetails): Promise<any> {


    await this.getRefreshToken();

    const normalizedUser = { ...user, email: this.normalizeEmail(user.email) };
    const headers = this.setAuthHeader();
    const params = this.params;
    const userId = this.cacheService.getObject<Token>('saras_auth_key').userId;
    return this.http
      .put<any>(this.appUrl + 'user/' + userId + '/child', normalizedUser, {
        headers: headers,
        params: params,
        observe: 'response',
      })
      .toPromise()
      .catch((err) => {
        this.handleErrorDontRedirect(err);
        this.underUserAddingProcess = false;
      });
  }

  async resendInvite(user: UserDetails): Promise<any> {


    await this.getRefreshToken();

    const normalizedUser = { ...user, email: this.normalizeEmail(user.email) };
    const headers = this.setAuthHeader();
    const params = this.params;
    const userId = this.cacheService.getObject<Token>('saras_auth_key').userId;
    return this.http
      .put<any>(this.appUrl + 'user/' + userId + '/resend-invite', normalizedUser, {
        headers: headers,
        params: params,
        observe: 'response',
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async removeUser(adminId: number, userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params.append('userId', userId.toString());
    return this.http
      .post(
        this.appUrl + 'user/' + adminId + '/remove',
        {},
        { headers: headers, params: params, observe: 'response' }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

   setPassword(user: UserPassword, code: string): Promise<any> {

  //  await this.getRefreshToken();

    const params = this.params;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'skip': 'true', //dont check if user is logged in, otherwise interceptor will take it to login page and will not make this api request
    });
    return this.http
      .put<any>(
        this.appUrl + 'user/' + user.userId + '/password/' + code,
        user,
        { headers: headers, params: params, observe: 'response' }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async createSource(source: Source): Promise<Source> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .put<any>(this.appUrl + 'source', source, { headers: headers })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async createSourceByPassAuth(sourceDetail: CreateSourceRequest): Promise<Source> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .put<any>(this.appUrl + 'source/create-source', sourceDetail, { headers: headers })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getSources(userId: number): Promise<Source[]> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params.append('userId', userId.toString());
    return this.http
      .get<Source[]>(this.appUrl + 'source', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getSourceCount(userId: number): Promise<number> {



   //

   // if(this.ifTokenIsExpired()) {     //to check if saved token is 1 hour old
      await this.getRefreshToken();

   // }

    const headers = this.setAuthHeader();
    const params = this.params.append('userId', userId.toString());
    return this.http
      .get<number>(this.appUrl + 'source/count', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getIntegrationNames(userId: number): Promise<string[]> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params.append('userId', userId.toString());
    return this.http
      .get<string[]>(this.appUrl + 'source/names', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getOrgIntegrationNames(userId: number): Promise<string[]> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params.append('userId', userId.toString());
    return this.http
      .get<string[]>(this.appUrl + 'source/OrgIntegrationNames', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async updateSource(sourceId: number, source: Source): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params.append('sourceId', sourceId.toString());
    return this.http
      .post(this.appUrl + 'source', source, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async deleteWarehouse(
    userId: number,
    warehouseId: number,
    actionType: string
  ): Promise<EnabledWarehouse[]> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .post(
        this.appUrl +
          'warehouse/' +
          userId.toString() +
          '/' +
          warehouseId.toString() +
          '/delete',
        actionType,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async updateSourceStatus(source: SourceUpdateData): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .put(this.appUrl + 'source/update', source, { headers: headers })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async restartOrphanJobs(): Promise<any> {

    await this.getRefreshToken();
    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'admin/restart-orphans', {})
      .toPromise()
      .catch((err) => this.handleError(err));

  }

  async cleanOrphanJobs(): Promise<any> {

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'admin/clean-orphans', { headers: headers })
      .toPromise()
      .catch((err) => this.handleError(err));

  }

  // async getFireTimeCall(userID: any, attemptCount = 3): Promise<any> {


  //   await this.getRefreshToken();

  //   const headers = this.setAuthHeader();
  //   const params = this.params;
  //   return this.http
  //     .get<any>(this.appUrl + 'integrations/' + userID + '/nextfiretime', {
  //       headers: headers,
  //       params: params,
  //     })
  //     .toPromise()
  //     .catch((err) => {
  //       if (attemptCount < 0) {
  //         const errorMessage = get(err, 'error.errors[0].message')
  //         if(errorMessage === `argument "content" is null`) {
  //           console.log('pipes down')
  //         } else {
  //           this.handleErrorDontRedirect(err);
  //         }
  //       }
  //       else { return Promise.resolve( this.getFireTimeCall(userID, --attemptCount) ) }
  //     });
  // }

  async renewToken(sourceId: number): Promise<any> {

    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<any>(this.appUrl + 'source/' + sourceId + '/refresh_token', {
        headers: headers,
        observe: 'response',
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getDataSources(params?: DataSourcesParams): Promise<Datasource[]> {
    await this.getRefreshToken();

    // Prepare query parameters
    let queryParams = new HttpParams();
    
    if (params) {
      if (params.status) queryParams = queryParams.set('status', params.status);
      if (params.parallelism !== undefined) queryParams = queryParams.set('parallelism', params.parallelism.toString());
      if (params.beta !== undefined) queryParams = queryParams.set('beta', params.beta.toString());
      if (params.templates !== undefined) queryParams = queryParams.set('templates', params.templates.toString());
      if (params.dashboard !== undefined) queryParams = queryParams.set('dashboard', params.dashboard.toString());
      if (params.datasourceIds && params.datasourceIds.length > 0) {
        params.datasourceIds.forEach(id => {
          queryParams = queryParams.append('datasourceIds', id.toString());
        });
      }
      if (params.includeSettings !== undefined) queryParams = queryParams.set('includeSettings', params.includeSettings.toString());
      if (params.type) queryParams = queryParams.set('type', params.type);
      if (params.displayName) queryParams = queryParams.set('displayName', params.displayName);
      if (params.logoUrl) queryParams = queryParams.set('logoUrl', params.logoUrl);
      if (params.loginType) queryParams = queryParams.set('loginType', params.loginType);
      if (params.displayCategory !== undefined) queryParams = queryParams.set('displayCategory', params.displayCategory.toString());
      if (params.cdkSource !== undefined) queryParams = queryParams.set('cdkSource', params.cdkSource.toString());
      if (params.datasourceCategory) queryParams = queryParams.set('datasourceCategory', params.datasourceCategory);
      if (params.types && params.types.length > 0) {
        params.types.forEach(type => {
          queryParams = queryParams.append('types', type);
        });
      }
    }

    // return this.http
    //   .get<Datasource[]>(this.appUrl + 'datasource/all', {})
    //   .toPromise()
    //   .catch((err) => this.handleError(err));
    return this.http
      .get<Datasource[]>(this.apiUrlSourceService + '/datasources/combined', {
        params: queryParams
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  getUrl(options: GetUrlOptions): string {
    const { isLegacy, isCdk, legacySourceId, purposeType, mode, migrated } = options;
   
    let studioApiURLSuffix = '';
    let sourceApiURLSuffix = '';
     switch (purposeType) {
      case 'integration':
        studioApiURLSuffix = 'integration-setup';
        sourceApiURLSuffix = 'integration-setups';
        break;

      case 'template':
       studioApiURLSuffix = 'template-setup';
       sourceApiURLSuffix = 'template-setups';
        break;
      default:
        break;
    }
    
    // When migrated=true, use new source service API
    if (migrated === true) {
      if (purposeType === 'template') {
        return `${this.studioApiUrl}/${studioApiURLSuffix}/`;
      } else if (purposeType === 'integration' && mode === 'attach') {
        return `${this.studioApiUrl}/${studioApiURLSuffix}/`;
      } else {
        return `${this.apiUrlSourceService}/${sourceApiURLSuffix}/`;
      }
    }
    
    if (isLegacy === true && isCdk === true) {
      return `${this.studioApiUrl}/${studioApiURLSuffix}/`;
    } else if (isLegacy === true && isCdk === false && legacySourceId) {
      return this.appUrl;
    } else if((isLegacy === false || isLegacy === null) && isCdk === true && purposeType === 'template') {
      return `${this.studioApiUrl}/${studioApiURLSuffix}/`;
    } else if((isLegacy === false || isLegacy === null) && isCdk === true && purposeType === 'integration' && mode === 'attach') {
      return `${this.studioApiUrl}/${studioApiURLSuffix}/`;
    } else if ((isLegacy === false || isLegacy === null) && isCdk === true) {
      return `${this.apiUrlSourceService}/${sourceApiURLSuffix}/`;
    } else {
      return this.appUrl;
    }
  }

  async getDataSource(dataSourceType: string): Promise<Datasource> {
    if (isMissingRouteParam(dataSourceType)) {
      throw new Error(`getDataSource called with invalid param: ${dataSourceType}`);
    }
    try {
      await this.getRefreshToken();
      // const headers = this.setAuthHeader();
      return await this.http
        .get<Datasource>(this.appUrl + 'datasource/' + dataSourceType, {}).pipe(
          rxjsMap((response: any) => {
            return {
              ...response,
              isLegacy: response.legacy,
              isCdk: response.cdk
            }
          })
        )
        .toPromise()
        .catch((err) => this.handleError(err));
    } catch (error) {
      console.error('Error getting data source:', error);
      throw error;
    }
  }

  async createWarehouse(warehouse: Warehouse): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .put<any>(this.appUrl + 'warehouse', warehouse, { headers: headers })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async checkPermission(sourceId: number, data: StepData[], currentSelection: string): Promise<any> {

    await this.getRefreshToken();
    const headers = this.setAuthHeader();

    return this.http
      .post<any>(this.appUrl + 'warehouse/' + sourceId + '/permission/' + currentSelection, data, {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.handleError(err));

  }

  async updateWarehouse(warehouseId: number, warehouse: Warehouse): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params.append('warehouseId', warehouseId.toString());
    return this.http
      .post(this.appUrl + 'warehouse/settings', warehouse, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getWarehouses(userId: number): Promise<Warehouse[]> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params.append('userId', userId.toString());
    return this.http
      .get<Warehouse[]>(this.appUrl + 'warehouse', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getWarehouse(warehouseId: number): Promise<Datawarehouse> {

    await this.getRefreshToken();

    return this.http
      .get<Datasource>(this.appUrl + 'datawarehouse/' + warehouseId, {})
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getEnabledWarehouseList(userId: number, attemptCount = 3): Promise<EnabledWarehouse[]> {
    await this.getRefreshToken();
  
    const headers = this.setAuthHeader();
    const params = this.params;
    try {
      const enabledWarehouses = await this.http
        .get<any>(`${this.appUrl}warehouse/${userId}/enabled`, {
          headers,
          params,
        })
        .toPromise();
      return this.attachPlanInfoToIntegrations(enabledWarehouses,'datawarehouse');
    } catch (err) {
      if (attemptCount < 0) {
        return this.handleErrorDontRedirect(err);
      } else {
        return this.getEnabledWarehouseList(userId, --attemptCount);
      }
    }
  }

  async getEnabledIntegrationsList(userId: number, attemptCount = 3): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'source/' + userId + '/enabled', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => {
          if( attemptCount < 0 ) {
            this.handleErrorDontRedirect(err)
          }
          this.getEnabledIntegrationsList(userId, --attemptCount)

      } );
  }

async getActiveIntegrationsList(userId: number, attemptCount = 3): Promise<any> {
  await this.getRefreshToken();
  const headers = this.setAuthHeader();
  const params = this.params;

  try {
    const activeIntegrations = await this.http
      .get<any>(`${this.appUrl}integrations/${userId}/active`, { headers, params })
      .toPromise();
    return this.attachPlanInfoToIntegrations(activeIntegrations);
  } catch (err) {
    if (attemptCount < 0) this.handleErrorDontRedirect(err);
    else return this.getActiveIntegrationsList(userId, --attemptCount);
  }
}

  async saveRestoreIdFreshChat(userId, restoreId): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .put<any>(
        this.appUrl + 'user/' + restoreId + '/restoreid/' + userId,
        {},
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async getActiveIntegrationsStatus(userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'integrations/' + userId + '/activestatus', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async getPendingIntegrationsList(userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'integrations/' + userId + '/pending', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async getPausedIntegrationsList(userId: number): Promise<any> {
    await this.getRefreshToken();
  
    const headers = this.setAuthHeader();
    const params = this.params;
    try {
      const pausedIntegrations = await this.http
        .get<any>(`${this.appUrl}integrations/${userId}/paused`, { headers, params })
        .toPromise();
      return this.attachPlanInfoToIntegrations(pausedIntegrations);
    } catch (err) {
      this.handleErrorDontRedirect(err);
    }
  }

  async getOnHoldIntegrations(userId: number): Promise<any> {
    await this.getRefreshToken();
    const headers = this.setAuthHeader();
    const params = this.params;
    try {
      const onHoldIntegrations = await this.http
        .get<any>(`${this.appUrl}integrations/${userId}/onhold`, { headers, params })
        .toPromise();
      return this.attachPlanInfoToIntegrations(onHoldIntegrations);
    } catch (err) {
      this.handleErrorDontRedirect(err);
    }
  }
  
  async getDeletedIntegrationsList(userId: number): Promise<any> {
    await this.getRefreshToken();
    const headers = this.setAuthHeader();
    const params = this.params;
    try {
      const deletedIntegrations = await this.http
        .get<any>(`${this.appUrl}integrations/${userId}/deleted`, { headers, params })
        .toPromise();
      return this.attachPlanInfoToIntegrations(deletedIntegrations);
    } catch (err) {
      this.handleErrorDontRedirect(err);
    }
  }

  async getDatawarehouses(): Promise<Datawarehouse[]> {
    // const headers = this.setAuthHeader();

    await this.getRefreshToken();

    return this.http
      .get<Datawarehouse[]>(this.appUrl + 'datawarehouse/all', {})
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async uploadFile(userId, file): Promise<void[]> {
     const headers = new HttpHeaders({})
     const params = this.params
     headers.append('Content-Type', 'multipart/form-data; boundary=<calculated when request is sent>');
    // params.append('Accept', 'application/json');
    headers.append('Content-Length', '<calculated when request is sent>');
    headers.append('Host', '<calculated when request is sent>');
    // let options = new RequestOptions({ headers: headers });
     let fileData:FormData = new FormData();
     fileData.append('file', file, file.name);

    await this.getRefreshToken();

    return this.http
      .post<void>(
        this.appUrl + 'source/file-upload/'+ userId.toString(),
        fileData,
        { headers: headers,  params: params}
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getUserSourceAccounts(sourceId: string): Promise<SourceAccount[]> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<string[]>(this.appUrl + 'source/' + sourceId + '/accounts', {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getUserWarehouseAccounts(
    warehouseId: string,
    userId: string
  ): Promise<WarehouseAccount[]> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<string[]>(
        this.appUrl + 'warehouse/' + warehouseId + '/' + userId + '/projects',
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getOrganizationUsers(adminId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<any>(this.appUrl + 'user/' + adminId + '/child', {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async logout(user: UserDetails): Promise<any> {


    await this.getRefreshToken();

    if (!this.cacheService.getObject<Token>('saras_auth_key')?.token) {
      // The header and account pages sign out of Firebase first, and that clears the auth key,
      // so there is no token to send: finish the local logout instead of throwing on the header.
      this.getUserLoginEvent().next(false);
      this.clearUserDetails();
      return undefined;
    }
    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'user/logout', user, { headers: headers })
      .toPromise()
      .then((v) => {
        this.getUserLoginEvent().next(false);
        // Clear userDetails from BehaviorSubject on logout
        this.clearUserDetails();
        return Promise.resolve(v);
      })
      .catch((err) => this.handleError(err));
  }

  businessesCarouselContent(): Promise<Array<any>> {
    // return this.http.get<TrustedBusinesses[]>(this.appUrl + 'businesses/carousel', {})
    // .toPromise();
    const data = [
      {
        image_url: 'https://www.capillarytech.com/img/CapillaryTech.svg',
      },
    ];
    return Promise.resolve(data);
  }

  abbreviateNumber(
    number,
    isTimeMs = false,
    notificationTime = false,
    timestamp = 0,
    timeZone = 'GMT'
  ) {
    if (timeZone === 'GMT') {
      timeZone = moment.tz.guess();
    }
    if (number === 0) {
      return 0;
    }
    if (isTimeMs && !notificationTime) {
      const conversionRate = 60000;
      const siSymbol = ['< 1 min', ' min'];
      // const siSymbol = ['', 'k', 'M', 'G', 'T', 'P', 'E'];
      const tier = number < conversionRate ? 0 : 1;
      const suffix = siSymbol[tier];
      const scale = tier === 0 ? 1 : conversionRate;
      const scaled = number / scale;
      return tier === 0 ? siSymbol[tier] : scaled.toFixed(1) + siSymbol[tier];
    } else if (isTimeMs && notificationTime) {
      const conversionRate = 60000;
      const siSymbol = ['just now', ' minutes ago', ' hours ago', ''];
      let tier = 0;
      let scale = 1;
      let scaled = 0;
      if (number < conversionRate) {
        tier = 0;
        scale = 1;
        return siSymbol[tier];
      } else if (number / conversionRate < 60) {
        tier = 1;
        scale = conversionRate;
        scaled = number / scale;
        if (Math.floor(scaled) === 1) {
          return Math.floor(scaled) + ' minute ago';
        }
        return scaled.toFixed(0) + siSymbol[tier];
      } else if (number / conversionRate < 24 * 60) {
        tier = 2;
        scale = conversionRate * 60;
        scaled = number / scale;
        if (Math.floor(scaled) === 1) {
          return Math.floor(scaled) + ' hour ago';
        }
        return Math.floor(scaled) + siSymbol[tier];
      } else {
        tier = 3;
        return moment(timestamp).tz(timeZone).format(this.titleTimeFormat);
      }
    } else {
      const siSymbol = ['', 'k', 'M'];
      // const siSymbol = ['', 'k', 'M', 'G', 'T', 'P', 'E'];
      let tier = Math.floor(Math.log10(number) / 3);
      if (tier === 0) {
        return number;
      } else if (tier > 2) {
        tier = 2;
      }

      const suffix = siSymbol[tier];
      const scale = Math.pow(10, tier * 3);
      const scaled = number / scale;
      return scaled.toFixed(1) + suffix;
    }
  }

  forgotPassword(userForgotPassword: UserForgotPassword): Promise<any> {
    const normalizedEmail = this.normalizeEmail(userForgotPassword.email);

    // this.getRefreshToken();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'skip': 'true'
    });
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'user/'+normalizedEmail+'/forgot', {
        headers: headers,
        params: params
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  getSignInEvent() {
    return this.signInEvent;
  }

  async getSubscriptionPlans(
    plantype: number,
    getAll: boolean
  ): Promise<Subscription[]> {

    await this.getRefreshToken();

    const headers = this.headers;
    return this.http
      .get<any>(
        this.appUrl +
          'subscriptions?plantype=' +
          plantype.toString() +
          '&getall=' +
          getAll,
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async updateUserSubscriptionPlan(data: CheckoutData): Promise<void> {

    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'user/subscription', data, {
        headers: headers,
        observe: 'response',
      })
      .toPromise()
      .catch((err) => this.handleTempError(err));
  }

  public set setCurrentSubscriptionPlan(plan){
    this._currentSubscription$.next(plan);
    this.currentSubscription = plan;
  }

  public get CurrentSubscriptionPlanGet(){
    return this.currentSubscription;
  }

  async getCurrentSubscriptionPlan(userId: number, attemptCount = 3): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<any>(this.appUrl + 'user/' + userId + '/subscription', {
        headers: headers,
        observe: 'response',
      })
      .toPromise()
      .catch((err) => {

        if( attemptCount < 0 ) { 
          // this.handleErrorDontRedirect(err); 
        }
        else { return Promise.resolve( this.getCurrentSubscriptionPlan(userId, --attemptCount) ) }

      } );
  }

  async generateHpURL(checkoutData: CheckoutData): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'subscriptions/generate_hp_url', checkoutData, {
        headers: headers,
      })
      .toPromise();
  }

  async generateHpCardURL(userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<any>(
        this.appUrl +
          'subscriptions/' +
          userId.toString() +
          '/generate_hp_url_card',
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async fetchDashboardData(
    from: number,
    to: number,
    companyId: number
  ): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(
        this.appUrl + 'integrations/' + companyId + '/stats',
        { from: from, to: to },
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async currentStats(companyId: number): Promise<CurrentCycleStats> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<any>(this.appUrl + 'integrations/' + companyId + '/current_cycle', {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async rowsProcessedHistory(
    companyId: number,
    from: number,
    to: number,
    type: number,
    attemptCount = 3
  ): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params
      .append('from', from.toString())
      .append('to', to.toString())
      .append('type', type.toString());
    return this.http
      .post<any>(
        this.appUrl + 'integrations/' + companyId + '/rows_processed',
        { from: from, to: to, type: type },
        { headers: headers }
      )
      .toPromise()
      //.catch((err) => this.handleErrorDontRedirect(err));
      .catch((err) => {
        if (attemptCount < 0) {
          this.handleErrorDontRedirect(err)
        } else {
            return Promise.resolve( this.rowsProcessedHistory(companyId, from, to, type, --attemptCount) )
        }

      });
  }

  async jobsProcessedHistory(
    companyId: number,
    from: number,
    to: number,
    type: number
  ): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params
      .append('from', from.toString())
      .append('to', to.toString())
      .append('type', type.toString());
    return this.http
      .post<any>(
        this.appUrl + 'integrations/' + companyId + '/jobs_processed',
        { from: from, to: to, type: type },
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async integrationsHistory(
    companyId: number,
    from: number,
    to: number,
    type: number
  ): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params
      .append('from', from.toString())
      .append('to', to.toString())
      .append('type', type.toString());
    return this.http
      .post<any>(
        this.appUrl + 'integrations/' + companyId + '/integrations_stats',
        { from: from, to: to, type: type },
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async jobProcessingTime(
    companyId: number,
    from: number,
    to: number,
    type: number
  ): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params
      .append('from', from.toString())
      .append('to', to.toString())
      .append('type', type.toString());
    return this.http
      .post<any>(
        this.appUrl + 'integrations/' + companyId + '/avg_jobs_processed',
        { from: from, to: to, type: type },
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async rowsRemaining(companyId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<any>(this.appUrl + 'integrations/' + companyId + '/rows_remaining', {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async logs(userId: number, top: number, skip: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<any>(
        this.appUrl +
          'user/' +
          userId.toString() +
          '/logs?top=' +
          top.toString() +
          '&skip=' +
          skip.toString(),
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  // logMessage(transactionLogId: number): Promise<any> {
  //   const headers = this.setAuthHeader();
  //   return this.http.get<any>(this.appUrl + 'user/log-message?id=' + transactionLogId.toString(), {headers: headers})
  //     .toPromise().catch((err) => this.handleErrorDontRedirect(err));
  // }

  async logMessageTable(transactionLogId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<any>(this.appUrl + 'user/log-messageTable?id=' + transactionLogId, {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async getStepData(sourceId: number, data: StepData[]): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'source/' + sourceId + '/step', data, {
//        .post<any>(this.appUrl + "source/" + sourceId + "/" + ifEditFlow + "/step", data, {

        headers: headers,
        observe: 'response',
      })
      .pipe(
        retryWhen(errors => errors.pipe(
          concatMap((error, index) => {
            // Retry only for 502 errors, max 3 attempts
            if (error?.status === 502 && index < 3) {
              return of(error).pipe(delay(2000));
            }
            // For other errors or after 3 attempts, throw the error
            return throwError(error);
          })
        ))
      )
      .toPromise()
      .catch((err) => this.stepDataError(err));
  }

  async getMetricsData(sourceId: number, data): Promise<any> {
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'source/' + sourceId + '/compatibleMetrics', data, {


        headers: headers,
        observe: 'response',
      })
      .toPromise()
      .catch((err) => this.stepDataError(err));
  }
  async validateGoogleAdsData(sourceId: number, tableName: String, data: StepData[]): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const requestOptions: Object = {
      headers: headers,
    }
    return this.http
      .post<any>(this.appUrl + 'source/' + sourceId + '/step/test/' + tableName, data, requestOptions)
      .toPromise()
      .catch((err) => this.stepDataError(err));
  }

  async saveInputAttr(userId: number, sourceId: number, data: StepData[], type: string, ifAuthDone): Promise<any>  {

    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'source/' + sourceId +'/' + userId + '/save-values/' + type +'/'+ ifAuthDone,
        data,
        {
          headers: headers,
        })
        .toPromise()
        .catch((err) => this.handleErrorDontRedirect(err));

  }


  async validateAttributeValue(sourceType: string, data: StepData[]): Promise<string> {


    await this.getRefreshToken();
    const headers = this.setAuthHeader();
    headers.append('Content-Type', 'application/text');
    const requestOptions: Object = {
      headers: headers,
      responseType: 'text'
    }
    return this.http
      .post<any>(this.appUrl + 'source/' + sourceType + '/validate', data, requestOptions)
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }


  async saveStepsData(sourceId: number, data: StepData[]): Promise<any> {


    await this.getRefreshToken();
    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'source/' + sourceId + '/save', data, {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async saveEditStepsData(
    sourceId: number,
    data: StepData[],
    reloadAttributes: string[],
    userId: number,
    changedTables: any
  ): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(
        this.appUrl + 'source/' + sourceId + '/' + userId + '/edit',
        {
          prevSteps: data,
          modifiedTables: reloadAttributes,
          changedTables: changedTables,
        },
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async onTableSync(
    tableId: number,
    syncDate: string,
    userId: number
  ): Promise<any> {

    await this.getRefreshToken();
    const headers = this.setAuthHeader();
    return this.http
      .post<any>(
        this.appUrl + 'source/syncFromDate/' + tableId + '?initiatedby=' + userId,
        {"syncDate":syncDate},
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async reloadTable(
    sourceId: number,
    userId: number,
    reloadTable: string, 
    sourceDetails?: any
  ): Promise<any> {
    let apiUrl;
    const isLegacy = get(sourceDetails, 'isLegacy', false);
    if(isLegacy){
      apiUrl = this.appUrl + 'source/' + sourceId + '/' + userId + '/reload'
    } else if(!isLegacy) {
      apiUrl = this.apiUrlSourceService + '/integration-setups/' + sourceId + '/' + userId + '/reload?productId=1'
    }
    
    await this.getRefreshToken();
    const headers = this.setAuthHeader();
    return this.http
      .post<any>(
        apiUrl,
        reloadTable,
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async getWarehouseStepData(warehouseId: number, data: StepData[]): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'warehouse/' + warehouseId + '/step', data, {
        headers: headers,
        observe: 'response',
      })
      .toPromise()
      .catch((err) => this.stepDataError(err));
  }

  async saveWarehouseStepsData(sourceId: number, data: StepData[]): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .post<any>(this.appUrl + 'warehouse/' + sourceId + '/save', data, {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

   fetchAllNotifications(
    since: number,
    userId: number
  ): Observable<Notification[]> {

     this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params
      .append('since', since.toString())
      .append('mode', 'UI');
    return this.http.get<Notification[]>(
      this.appUrl + 'user/' + userId + '/notification',
      { headers: headers, params: params }
    );
  }
  async clearAllNotifications(userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .post<any>(
        this.appUrl + 'user/' + userId + '/closeAllNotification ',
        { result: 'yes' },
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async fetchIntegrationDetails(sourceId: number, userId: number, top, skip): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    // http://localhost:8080/integrations/300/1/integration-details?top=2&skip=1
    return this.http
      .get<any>(
        this.appUrl +
          'integrations/' +
          sourceId +
          '/' +
          userId +
          '/integration-details?top=' +
          top.toString() +
          '&skip=' +
          skip.toString(),
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async closeNotification(
    notificationId: number,
    userId: number,
    result: NotificationResult
  ): Promise<void> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params.append(
      'notificationId',
      notificationId.toString()
    );
    return this.http
      .post<void>(
        this.appUrl + 'user/' + userId + '/closeNotification',
        result,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  encodeUri(component: string): string {
    return this.codec.encodeValue(component);
  }

  async fetchAllIntegrations(userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'user/' + userId + '/transactions', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async fetchIntegrationConfig(sourceId: number, userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'source/' + userId + '/' + sourceId + '/created', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }
  async fetchIntegrationConfigStatus(transactionId: number): Promise<IntegrationConfigStatus> {
    
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
     .get<any>(this.appUrl + 'integrations/job/' + transactionId,
      // .get<any>(`${this.appUrl}/${jobId}`, 
      { headers: headers, params: params })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  fetchIntegrationTableCategory(sourceId: number, tableName: string): Observable<ReportCategory> {
    return from(this.getRefreshToken())
        .pipe(
            switchMap(() => {
                const headers = this.setAuthHeader();
                const params = this.params;

                return this.http.get<ReportCategory>(this.appUrl + 'integrations/sourceid/' + sourceId + '/tablename/' + tableName + '/report-category', {
                    headers: headers,
                    params: params,
                    responseType: 'json'
                });
            }),
            catchError((err) => this.handleErrorDontRedirect(err))
        );
}

  async fetchIntegrationConfigClone(sourceId: number, userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'source/' + userId + '/' + sourceId + '/clone', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async fetchWarehouseConfig(warehouseId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'warehouse/' + warehouseId + '/created', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async fetchIntegrationTables(sourceId: number, userId: number, attemptCount = 3): Promise<TableNames> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'source/' + userId + '/' + sourceId + '/configured', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => {
        if (attemptCount < 0) {
          this.handleErrorDontRedirect(err)
        } else {
            return Promise.resolve( this.fetchIntegrationTables(sourceId, userId, --attemptCount) )
        }

      });
  }

  async getLegacyIntegrationTables(
  sourceId: number, 
  searchQuery: string = '*', 
  isAmazonSource: boolean = false
): Promise<any> {
  await this.getRefreshToken();
  
  const headers = this.setAuthHeader();
  const params = new HttpParams()
    .set('searchQuery', searchQuery)
    .set('isAmazonSource', isAmazonSource.toString());

  return this.http
    .get<any>(`${environment.apiUrl}legacy-integration-tables/integration/${sourceId}`, {
      headers: headers,
      params: params
    })
    .toPromise()
    .catch((err) => this.handleErrorDontRedirect(err));
}

  async changeTimeZone(timeZone: string, userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .post<any>(this.appUrl + 'user/' + userId + '/preferences', timeZone, {
        headers: headers,
        params: params,
        observe: 'response',
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async getPreferences(userId: number, attemptCount = 3): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(
        this.appUrl + 'preferences/' + userId.toString() + '/getpreferences',
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => {
        if (attemptCount < 0 ) { this.handleError(err); }
        else {
          return Promise.resolve( this.getPreferences(userId, --attemptCount) )
        }
      });
  }
  async getPreferencesNew(userId: number, attemptCount = 3): Promise<any> {

    const tempUrl = "https://dev-notifications.sarasanalytics.com/v1/api/"
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get<any>(
        tempUrl + 'preferences/general/' + userId.toString(),
        { headers: headers }
      )
      .toPromise()
      .catch((err) => {
        if (attemptCount < 0 ) { this.handleError(err); }
        else {
          return Promise.resolve( this.getPreferencesNew(userId, --attemptCount) )
        }
      });
  }

  async submitPreferences(userId: number, groupId, channel, selected: boolean){
    const tempUrl = "https://dev-notifications.sarasanalytics.com/v1/api/"
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = {
      userId : userId,
      groupId: parseInt(groupId),
      channel,
      selected
    }
    return this.http
      .put<any>(
        tempUrl + 'preferences/general?' + `userId=${userId}&groupId=${groupId}&channel=${channel}&selected=${selected}`,
        {headers}
      ).toPromise()
      .catch(err  => {
        console.log('error from submission api', err);
      });
  }

  async getPreferenceById(userId: number, pId: number, attemptCount = 3): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(
        this.appUrl + 'preferences/' + userId.toString() + '/1/getPreference',
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => {
        if (attemptCount < 0) {
          this.handleError(err)
        } else {
          return Promise.resolve( this.getPreferenceById(userId, pId, --attemptCount) )
        }

      });
  }

  async updatePreference(userId: number, data: any): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .post<Token>(
        this.appUrl + 'preferences/' + userId.toString() + '/setpreferences',
        data,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getNotifications_vm(userId: number, paginationInfo = {}, fetchParams?): Promise<any>  {
    //  await this.getRefreshToken();

     const headers = this.setAuthHeader();
     let params;
     if (fetchParams) {
       params = this.params.set(fetchParams.type, fetchParams.id.toString());
     } else {
       params = this.params.set('user-id', userId.toString());
     }
      params = params.append('page', paginationInfo['pageIndex'])
            .append('size', paginationInfo['pageSize'])//.set("type", "*")

    return this.http
      .get(this.vmUrl + 'panel/panel-notifications', {
        headers: headers,
        params: params
      })
      .pipe(
        catchError((err) => {
          this.handleError(err);
          return of(null);
        })
      )
      .toPromise();

  }

  async getAdminEmail(createdBy: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    headers.append('Content-Type', 'application/text');
    return this.http
      .get(this.appUrl + 'user/' + createdBy.toString() + '/admin-email', {
        headers: headers,
        params: params,
        responseType: 'text'
      })
      .toPromise();
  }


  async getTrialPlan(userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'user/' + userId.toString() + '/trialplan', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async activateTrialPlan(userId: number, trialplan): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .put<string>(
        this.appUrl + 'user/' + userId.toString() + '/trailenabled',
        trialplan,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getCardDetails(userId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'user/' + userId.toString() + '/card_details', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async cardUpdate(data: any): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .post<any>(
        this.appUrl + 'subscriptions/' + data.userId + '/card_update',
        data,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getUserByUid(uid: string) {

    await this.getRefreshToken();

    const headers = this.headers;
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'user?uid=' + uid + '/firebaseId', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }


  async verifyFirebaseToken(firetoken: string): Promise<Token> {
    // Optional: Uncomment if token refresh is needed before verification
    // await this.getRefreshToken();
  
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'skip': 'true'
    });
    const params = this.params.set('firebasetoken', firetoken);
  
    try {
      // Attempt to verify the Firebase token. Status 0 / 5xx is often a pod restarting behind the
      // load balancer, so retry briefly before treating it as an outage.
      for (let attempt = 1; ; attempt++) {
        try {
          return await this.http.post<Token>(`${this.appUrl}user/verifyfiretoken`, {}, { headers, params }).toPromise();
        } catch (attemptError) {
          const transient = attemptError.status === 0 || attemptError.status >= 500;
          if (!transient || attempt >= AppService.VERIFY_TOKEN_ATTEMPTS) {
            throw attemptError;
          }
          await new Promise((resolve) => setTimeout(resolve, AppService.VERIFY_TOKEN_RETRY_DELAY_MS * attempt));
        }
      }
  
    } catch (err) {
      // Handle specific server errors (status 500 and 0)
      if (err.status === 0 || err.status >= 500) {
        this.maintenanceMode$.next(true); // Activate maintenance mode for critical errors
      }
      // Pass the error to handleError for additional processing/logging
      return this.handleError(err, false, { data: { firebasetoken: firetoken } });
    }
  }

  getCustomToken(uid: string, email: string, name: string, roles: string[]){
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'skip': 'true'
    });
    const body = {
      uid, email, name, roles
    }
    return this.http.post<Token>(`${this.userServiceUrl}/user/firebase-custom-token`, body, {headers}).toPromise();
  }
  

  /**
   * Fetches Admin integrations matching the selected filters.
   * @param filterCriteria Filters applied to the Admin integration list.
   * @param useNewPlatform Whether to use source-service instead of the legacy API.
   * @param page Zero-based source-service page index.
   * @param pageSize Number of source-service records requested per page.
   * @returns The matching integrations and their total count.
   */
  getRunningSources(
    filterCriteria: AdminIntegrationFilterCriteria,
    useNewPlatform = false,
    page = 0,
    pageSize = 5
  ): Observable<AdminIntegrationPage> {
    this.getRefreshToken();
    const headers = this.setAuthHeader();
    let params = this.params
      .append('company', filterCriteria.company)
      .append('email', filterCriteria.email)
      .append('sourceType', filterCriteria.sourceType.toString())
      .append('sourceId', filterCriteria.sourceId)
      .append('sourceName', filterCriteria.sourceName);

    if (useNewPlatform) {
      params = params
        .append('page', page.toString())
        .append('size', pageSize.toString());
      return this.http.get<RunningSource[]>(
        `${this.adminIntegrationsUrl}/running/${filterCriteria.sourceStatus}`,
        { headers, params, observe: 'response' }
      ).pipe(
        rxjsMap(response => ({
          integrations: response.body || [],
          ...this.getAdminIntegrationPagination(response, page, pageSize)
        }))
      );
    }

    const status = parseInt(String(filterCriteria.sourceStatus), 10);
    return this.http.get<RunningSource[]>(
      `${this.appUrl}source/runningsources/${status}`,
      { headers, params }
    ).pipe(
      rxjsMap(integrations => ({
        integrations,
        total: integrations.length,
        totalIsExact: true
      }))
    );
  }

  /**
   * Marks an Admin integration as modified.
   * @param sourceId Integration identifier.
   * @param useNewPlatform Whether to use source-service instead of the legacy API.
   * @param adminUserId User initiating the source-service action.
   * @returns A promise that settles when the request finishes.
   */
  async touchSource(
    sourceId: number,
    useNewPlatform = false,
    adminUserId?: string
  ): Promise<void> {
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = adminUserId && useNewPlatform
      ? this.params.set('adminUserId', adminUserId)
      : this.params;
    const url = useNewPlatform
      ? `${this.adminIntegrationsUrl}/${sourceId}/touch`
      : `${this.appUrl}source/${sourceId}/touch`;
    await this.http
      .post<void>(
        url,
        {},
        { headers, params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async performLimitCheck(userId:number,sourceId:number): Promise<any> {
    const headers=this.setAuthHeader();
    const params=this.params;
    this.http
      .get(this.appUrl + 'source/'+userId+'/'+sourceId+'/performLimitCheck', { headers: headers })
      .toPromise()
      .catch((err) => this.handleError(err));
  }


  async getAllEmails(): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get(this.appUrl + 'source/email', { headers: headers })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  /**
   * Fetches source options used by the Admin integration filters.
   * @param useNewPlatform Whether to use source-service instead of the legacy API.
   * @returns Source identifiers and names available to the Admin user.
   */
  async getAllSources(
    useNewPlatform = false
  ): Promise<AdminIntegrationSourceOption[]> {
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    if (useNewPlatform) {
      return this.getAllAdminSourceOptions(headers);
    }
    return this.http
      .get<AdminIntegrationSourceOption[]>(
        `${this.appUrl}source/allsources`,
        { headers, params: this.params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  private async getAllAdminSourceOptions(
    headers: HttpHeaders
  ): Promise<AdminIntegrationSourceOption[]> {
    const sourceOptions: AdminIntegrationSourceOption[] = [];
    let page = 0;
    let pageOptions: AdminIntegrationSourceOption[];
    do {
      const params = this.params
        .set('page', page.toString())
        .set('size', ADMIN_SOURCE_OPTIONS_PAGE_SIZE.toString());
      pageOptions = await this.http
        .get<AdminIntegrationSourceOption[]>(
          `${this.adminIntegrationsUrl}/all`,
          { headers, params }
        )
        .toPromise()
        .catch((err) => this.handleError(err));
      sourceOptions.push(...(pageOptions || []));
      page += 1;
    } while (pageOptions?.length === ADMIN_SOURCE_OPTIONS_PAGE_SIZE);
    return sourceOptions;
  }

  async getIntegrationStatus(userId): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get(this.appUrl + 'integrations/' + userId + '/sourcestatus', {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getPreferenceForRowsProcessedColumn(userId): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get(
        this.appUrl +
          'integrations/' +
          userId +
          '/is-rows-processed-list-active',
        { headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getRowsProcessedForEachIntegration(companyId, from, to): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params.set('from', from).set('to', to);
    return this.http
      .get(
        this.appUrl +
          'integrations/' +
          companyId +
          '/rows-processed-each-integration',
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  /**
   * Stops a running Admin integration.
   * @param sourceId Integration identifier.
   * @param useNewPlatform Whether to use source-service instead of the legacy API.
   * @param adminUserId User initiating the source-service action.
   * @returns A promise that settles when the request finishes.
   */
  async stopSource(
    sourceId: number,
    useNewPlatform = false,
    adminUserId?: string
  ): Promise<void> {
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = adminUserId && useNewPlatform
      ? this.params.set('adminUserId', adminUserId)
      : this.params;
    const url = useNewPlatform
      ? `${this.adminIntegrationsUrl}/${sourceId}/stop`
      : `${this.appUrl}source/${sourceId}/stop`;
    await this.http
      .post<void>(
        url,
        {},
        { headers, params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  /**
   * Starts an integration using the selected Admin service contract.
   * @param sourceId Integration identifier.
   * @param userId User initiating the action.
   * @param sourceMetadata Metadata used for non-Admin source routing.
   * @param routing Explicit Admin routing, or automatic source routing.
   * @returns A promise that settles after the start request completes (or fails).
   */
  async startSource(
    sourceId: number,
    userId: string,
    sourceMetadata?: SourcePlatformMetadata,
    routing: StartSourceRouting = 'auto'
  ): Promise<void> {
    if (routing === 'admin-new') {
      await this.startAdminSource(sourceId, userId);
      return;
    }
    if (routing === 'admin-legacy') {
      await this.startSourceSync(sourceId, userId);
      return;
    }
    if(shouldUseSourceService(sourceMetadata || {})) {
      // Resolves once the request has been answered (or failed) so a caller that leaves the
      // page afterwards (iQ setup "Continue in iQ") does not cancel it; others ignore the promise.
      await new Promise<void>((resolve) => {
        this.sourceGraphQLService.syncIntegrations([sourceId], Number(userId), 1).pipe(rxjsFirst()).subscribe({
          complete: () => resolve(),
          error: (err) => {
            this.handleError(err, false);
            resolve();
          }
        });
      });
    } else {
      await this.startSourceSync(sourceId, userId);
    }
  }

  private async startAdminSource(
    sourceId: number,
    adminUserId?: string
  ): Promise<void> {
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = adminUserId
      ? this.params.set('adminUserId', adminUserId)
      : this.params;
    await this.http
      .post<void>(
        `${this.adminIntegrationsUrl}/${sourceId}/start`,
        [],
        { headers, params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async startSourceSync(sourceId: number, userId: string): Promise<any> {
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    let data = {
      'tables': [],
      'initiatedBy': userId
    }
    return await this.http
      .post<any>(
        this.appUrl + 'source/' + sourceId.toString() + '/start',
        data,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async syncAtTableLevel(sourceId: number, tableList: any[], userId: string, sourceMetaData?: any): Promise<any> {
    if(shouldUseSourceService(sourceMetaData || {})) {
      return await this.sourceGraphQLService
        .syncTables(tableList[0]?.tableName, [sourceId], Number(userId), 1)
        .pipe(rxjsFirst())
        .toPromise()
        .catch((err) => this.handleError(err));
    } else {
      return await this.syncAtTableLevelLegacy(sourceId, tableList, userId);
    }
  }

  async syncAtTableLevelLegacy(sourceId: number, tableList: any[], userId: string): Promise<any> {
    await this.getRefreshToken();
    let data = {
      'tables': tableList,
      'initiatedBy': userId
    }
    const headers = this.setAuthHeader();
    const params = this.params//.append("schema", tableSchema.toString()).append("tablename", tableName.toString()) ;
    return await this.http
      .post<any>(
        this.appUrl + 'source/' + sourceId.toString() + '/start',
        data,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  /**
   * Fetches the stored JSON configuration for an Admin integration.
   * @param sourceId Integration identifier.
   * @param useNewPlatform Whether to use source-service instead of the legacy API.
   * @returns The integration's JSON configuration.
   */
  async getSourceJson(
    sourceId: number,
    useNewPlatform = false
  ): Promise<Record<string, unknown>> {
    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const url = useNewPlatform
      ? `${this.adminIntegrationsUrl}/${sourceId}/json`
      : `${this.appUrl}source/${sourceId}/json`;
    return this.http
      .get<Record<string, unknown>>(url, { headers })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  /**
   * Derives paginator totals from the response header or a page-size sentinel.
   * @param response HTTP response from the Admin integration search API.
   * @param page Zero-based page index requested.
   * @param pageSize Number of records requested for the page.
   * @returns Total count and whether it is exact.
   */
  private getAdminIntegrationPagination(
    response: HttpResponse<RunningSource[]>,
    page: number,
    pageSize: number
  ): Pick<AdminIntegrationPage, 'total' | 'totalIsExact'> {
    const headerValue = response.headers.get('X-Total-Count');
    const headerTotal = Number(headerValue);
    if (
      headerValue !== null &&
      Number.isFinite(headerTotal) &&
      headerTotal >= 0
    ) {
      return { total: headerTotal, totalIsExact: true };
    }
    const resultCount = response.body?.length ?? 0;
    const loadedCount = (page * pageSize) + resultCount;
    const totalIsExact = resultCount < pageSize;
    return {
      total: totalIsExact ? loadedCount : loadedCount + 1,
      totalIsExact
    };
  }

  async getLastSyncInfo(userId, attemptCount = 3): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get(this.appUrl + 'source/' + userId + '/lastsyncinfo', {
        headers: headers,
      })
      .toPromise()
      .catch((err) => {
        // if( attemptCount < 0 ) {
        //   this.handleError(err)
        // }
        // this.getLastSyncInfo(userId, --attemptCount)
        if(attemptCount < 0) { this.handleError(err) }
        else {
          return Promise.resolve( this.getLastSyncInfo(userId, --attemptCount) )
        }
      });
  }



  async getLastSyncInfo_v2(userId, sourceIds): Promise<any> {


    await this.getRefreshToken();
    const headers = this.setAuthHeader();
    const params = this.params//.set("sourceIds", sourceIds);
    return this.http
      .post(this.appUrl + 'source/v2/lastsyncinfo', sourceIds, {
        headers: headers, params: params
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getErrorTables(sourceId): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    return this.http
      .get(this.appUrl + 'source/' + sourceId + '/errortables', {
        headers: headers,
      })
      .toPromise()
      .catch((err) => this.handleError(err));
  }

  async getIntegrationInfo(sourceId: number, userId: number, attemptCount = 3): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    // http://localhost:8080/integrations/300/1/integration-details?top=2&skip=1
    return this.http
      .get<any>(
        this.appUrl +
          'integrations/' +
          sourceId +
          '/' +
          userId +
          '/integration-info',
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => {
        if(attemptCount < 0) { this.handleErrorDontRedirect(err) }
        else {
          return Promise.resolve( this.getIntegrationInfo(sourceId, userId, --attemptCount))
        }
      });
  }

  async getFacilityError(sourceId: number): Promise<any> {

    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'integrations/uniware/' + sourceId + '/facility-error', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));

  }

  async getLastFireInfo(sourceId: number, attemptCount = 3): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'source/' + sourceId + '/lastsync-sourceinfo', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => {
        if (attemptCount < 0) { this.handleErrorDontRedirect(err) }
        else { this.getLastFireInfo(sourceId, --attemptCount) }
      });
  }

  async getNextFireInfo(sourceId: number, attemptCount = 3, sourceDetails?: any): Promise<any> {

    let apiUrl; 
    const isLegacy = sourceDetails?.isLegacy;

    if(isLegacy){
      apiUrl = this.appUrl + 'integrations/' + sourceId + '/nextfiretimesource';
    } else {
      apiUrl = `${this.apiUrlSourceService}/integration-details/integrations/${sourceId}/next-fire-time?productId=1`;
    }
    // {{sourceServiceUrl}}/v1/api/integration-details/integrations/12/next-fire-time?productId=1.

    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(
        apiUrl,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => {
        if (attemptCount < 0) { this.handleErrorDontRedirect(err); }
        else {
          return Promise.resolve( this.getNextFireInfo(sourceId, --attemptCount, sourceDetails))
        }
      });
  }

  async getTableList(sourceId: number, searchQuery, ifAmazonSource, attemptCount = 3): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    const top = 20;
    const skip = 0;
    return this.http
      .get<any>(
        this.appUrl +
          'integrations/' +
          sourceId +
          '/integration-tables?top=' +
          top.toString() +
          '&skip=' +
          skip.toString() +
          '&searchQuery=' +
          searchQuery.toString()+
          '&ifAmazonSource=' +
          ifAmazonSource,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => {
        if (attemptCount < 0) { this.handleErrorDontRedirect(err) }
        else {
          return Promise.resolve ( this.getTableList(sourceId, searchQuery, ifAmazonSource, --attemptCount) )
        }
      });
  }

  async getJobs(userId, sourceId, tablename, startTime, endTime, type, attemptCount = 3): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(
        this.appUrl +
          'integrations/' +
          sourceId +
          '/transaction-logs/' +
          userId +
          '?tablename=' +
          tablename.toString() +
          '&startDate=' +
          startTime.toString() +
          '&endDate=' +
          endTime.toString() +
          '&type=' +
          type.toString(),
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => {
        if (attemptCount < 0 ) { this.handleErrorDontRedirect(err) }
        else {
          return Promise.resolve(this.getJobs(userId, sourceId, tablename, startTime, endTime, type, --attemptCount))
        }
      } );
  }

  async getLastRecord(sourceId, attemptCount = 3): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get(this.appUrl + 'integrations/' + sourceId + '/lastrecord', {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => {
        if (attemptCount < 0) {
          this.handleError(err)
        } else {
          return Promise.resolve( this.getLastRecord(sourceId, --attemptCount) )
        }

      });
  }

  /**
   * Fetches the current sync status for a single integration source.
   * Refreshes the auth token before each attempt and retries up to 3 times
   * with exponential backoff (1s, 2s, 4s). Returns `null` on exhausted retries.
   * @param sourceId - The numeric or string identifier of the integration source.
   * @param sourceMetaData - Optional metadata object describing the integration source.
   * @returns An Observable emitting the source status object, or `null` on failure.
   */
  getSingleIntegrationStatus(sourceId: number | string, sourceMetaData?: Record<string, any>): Observable<any> {
    return defer(() => this.getRefreshToken()).pipe(
      switchMap(() => {
        const headers = this.setAuthHeader();
        return this.http.get(`${this.appUrl}integrations/${sourceId}/single-sourcestatus`, {
          headers,
        });
      }),
      retryWhen(errors => errors.pipe(
        switchMap((err, retryCount) => retryCount < 3 ? timer(Math.min(1000 * Math.pow(2, retryCount), 30000)) : throwError(err))
      )),
      catchError((err) => {
        this.handleError(err);
        return of(null);
      })
    );
  }

  // async getCdkLastRecord(sourceId, attemptCount = 3): Promise<any> {
  //   const headers = this.setAuthHeader();
  //   const params = this.params;
  //   return this.http
  //     .get(this.appUrl + 'connector-studio/get-all-tables-completely-processed-ranges?sourceId=' + sourceId, {
  //       headers: headers,
  //       params: params,
  //     })
  //     .toPromise()
  //     .catch((err) => {
  //       if (attemptCount < 0) {
  //         this.handleError(err)
  //       } else {
  //         return Promise.resolve( this.getCdkLastRecord(sourceId, --attemptCount) )
  //       }
  //     });
  // }

  // async getCdkTableSpecificLastRecord(tableId, attemptCount = 3): Promise<any> {
  //   const headers = this.setAuthHeader();
  //   const params = this.params;
  //   return this.http
  //     .get(this.appUrl + 'connector-studio/get-completely-processed-range?tableId=' + tableId, {
  //       headers: headers,
  //       params: params,
  //     })
  //     .toPromise()
  //     .catch((err) => {
  //       if (attemptCount < 0) {
  //         this.handleError(err)
  //       } else {
  //         return Promise.resolve( this.getCdkLastRecord(tableId, --attemptCount) )
  //       }
  //     });
  // }
  
  async getRowsProcessedForSource(
    companyId: number,
    from: number,
    to: number,
    sourceId: number
  ): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params
      .append('from', from.toString())
      .append('to', to.toString());
    //  .append('sourceId', sourceId.toString());
    return this.http
      .get<any>(
        this.appUrl +
          'integrations/' +
          sourceId +
          '/' +
          companyId +
          '/graphdata',
        { params: params, headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async getRowsProcessedForSourceByDay(
    companyId: number,
    from: number,
    to: number,
    sourceId: number
  ): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params
      .append('from', from.toString())
      .append('to', to.toString());
    //  .append('sourceId', sourceId.toString());
    return this.http
      .get<any>(
        this.appUrl +
          'integrations/' +
          sourceId +
          '/' +
          companyId +
          '/graphdatabyday',
        { params: params, headers: headers }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async fetchPreConfiguredGATables(sourceId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(
        this.appUrl + 'source/' + sourceId + '/ga-preconfigured-tables',
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async fetchTableReportStatus(sourceid: number, tablename: string, starttime: number, endtime: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'integrations' + '/reportjobstatus/' + sourceid.toString() +
            '/' + tablename + '/' +starttime.toString()
            + '/' + endtime.toString() , {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async fetchJobReportStatus(jobId: number): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
    return this.http
      .get<any>(this.appUrl + 'integrations' + '/reportjobstatus/' + jobId, {
        headers: headers,
        params: params,
      })
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }


  async verifyFreshWorkCRM(apiKey,module_name, subdomain, sourceName): Promise<any> {


    await this.getRefreshToken();

    const headers = this.setAuthHeader();
    const params = this.params;
   // const params = this.params;
  //  return this.http
      // .get<any>("https://signinhype.myfreshworks.com/crm/sales/api/" + module_name +"/filters", {
      //   headers: headers,
      //   params: params,
      // })
      return this.http.get<RunningSource[]>(
        this.appUrl + 'source/verify-freshsource/' + apiKey + '/' + module_name+ '/' + subdomain + '/'+sourceName,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));

  }

  async getSavedSettingPrimaryFields(sourceId: number): Promise<any> {


   // if(this.ifTokenIsExpired()) {     //to check if saved token is 1 hour old
      await this.getRefreshToken();

    //}

    const headers = this.setAuthHeader();
    const params = this.params;
    // http://localhost:8080/integrations/300/1/integration-details?top=2&skip=1 endpoint : {sourceId}/savedSettings
    return this.http
      .get<any>(
        this.appUrl +
          'source/' +
          sourceId +
          '/savedSettings' ,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  async getActiveSources(userId: number, attemptCount = 3): Promise<any> {
    await this.getRefreshToken();
  
    const headers = this.setAuthHeader();
    const params = this.params;
    try {
      const activeSources = await this.http
        .get<any>(`${this.appUrl}source/${userId}/active-sources`, {
          headers,
          params
        })
        .toPromise(); 
      return this.attachPlanInfoToIntegrations(activeSources);
    } catch (err) {
      if (attemptCount < 0) {
        return this.handleErrorDontRedirect(err);
      } else {
        return this.getActiveSources(userId, --attemptCount);
      }
    }
  }

  async getValidBreakDownActionBreakDown(): Promise<any> {


   // if(this.ifTokenIsExpired()) {     //to check if saved token is 1 hour old
      await this.getRefreshToken();

    //}

    const headers = this.setAuthHeader();
    const params = this.params;
    // http://localhost:8080/integrations/300/1/integration-details?top=2&skip=1 endpoint : {sourceId}/savedSettings
    return this.http
      .get<any>(
        this.appUrl +
          'datasource/fb/validBreakdownCombinations' ,
        { headers: headers, params: params }
      )
      .toPromise()
      .catch((err) => this.handleErrorDontRedirect(err));
  }

  // fetchJobReportStatus(jobId: number): any {/datasource/fb/validBreakdownCombinations

  //   const headers = this.setAuthHeader();
  //   const params = this.params;
  //   return this.http.get<any>(this.appUrl + 'integrations' +
  //     '/reportjobstatus/' + jobId,
  //     {headers: headers, params: params}).toPromise().catch((err) => this.handleErrorDontRedirect(err));

  // }

  timeConversion(timestamp, conversionFor = 'first') {
    if(timestamp === null) {
      return 'Scheduled';
    }
    const millisec =
      conversionFor === 'first'
        ? timestamp - Date.now()
        : Date.now() - timestamp;

    const seconds = parseInt((millisec / 1000).toFixed(1), 10);

    const minutes = parseInt((millisec / (1000 * 60)).toFixed(1), 10);

    const hours = parseInt((millisec / (1000 * 60 * 60)).toFixed(1), 10);

    const days = parseInt((millisec / (1000 * 60 * 60 * 24)).toFixed(1), 10);

    const weeks = parseInt(
      (millisec / (1000 * 60 * 60 * 24 * 7)).toFixed(1),
      10
    );

    const months = parseInt(
      (millisec / (1000 * 60 * 60 * 24 * 7 * 4)).toFixed(1),
      10
    );

    const years = parseInt(
      (millisec / (1000 * 60 * 60 * 24 * 7 * 4 * 12)).toFixed(1),
      10
    );

    if (seconds < 60) {
      // return seconds + " Sec";
      return seconds > 1 ? `${seconds} seconds` : `${seconds} second`;
    } else if (minutes < 60) {
      //  return minutes + " Min";
      return minutes > 1 ? `${minutes} minutes` : `${minutes} minute`;
    } else if (hours < 24) {
      // return hours + " Hrs";
      return hours > 1 ? `${hours} hours` : `${hours} hour`;
    } else if (days < 7) {
      // return days + " Days";
      return days > 1 ? `${days} days` : `${days} day`;
    } else if (weeks < 4) {
      // return weeks + " weeks";
      return weeks > 1 ? `${weeks} weeks` : `${weeks} week`;
    } else if(months < 12) {
      // return months + " months";
      return months > 1 ? `${months} months` : `${months} month`;
    } else {
      return years > 1 ? `${years} years` : `${years} year`;
    }
  }

  notificationTitle(number, timestamp = 0, timeZone = 'GMT') {
    if (timeZone === 'GMT') {
      timeZone = moment.tz.guess();
    }
    const conversionRate = 60000;
    if (
      number < conversionRate ||
      number / conversionRate < 60 ||
      number / conversionRate < 24 * 60
    ) {
      return 'Today';
    } else if (number / conversionRate < 24 * 60 * 2) {
      return 'Yesterday';
    } else {
      return moment(timestamp).tz(timeZone).format(this.titleTimeFormat); // moment(timestamp).tz(timeZone).format(this.timeFormat);
    }
  }

    getSourceLogs(sourceId: number, transactionLogId: number, tableName: string){

    //
    // if(this.ifTokenIsExpired()) {     //to check if saved token is 1 hour old
        this.getRefreshToken();

    // }

    const headers = this.setAuthHeader();
    const url = this.appUrl + 'source/' + sourceId + '/log';
    const params = new HttpParams()
      .append('transactionLogId', transactionLogId.toString())
      .append('table', tableName);
    return this.http.get<SourceTransactionLog[]>(url, {
      headers: headers,
      params: params,
    });
  }

  setAgentTemplate(sourceId: number, template: any, type: string, companyId: any): Promise<any> {
    const url = `${this.appUrl}source/set-template/${sourceId}`;
    const headers = this.setAuthHeader();
    const params = new HttpParams()
      .append('template', template)
      .append('type', type)
      .append('companyId', companyId);
      return this.http.get<any>(url, {
        headers: headers,
        params: params,
        observe: 'response',
      }).toPromise();
  }

  /**
   * Writes a fresh ID token into the cached auth key.
   *
   * @returns The stored token, or null when there is no cached key yet (not signed in to Daton).
   */
  private storeRefreshedToken(idToken: string): Token | null {
    const cached = this.cacheService.getObject<Token>('saras_auth_key');
    if (!cached) {
      return null;
    }
    const token = new Token();
    token.role = cached.role;
    token.token = idToken;
    token.userId = cached.userId;
    this.cacheService.putObject('time-laps', new Date().getTime(), true);
    this.cacheService.putObject('saras_auth_key', token, true);
    return token;
  }

  /**
   * Runs `attempt` now and then every `AUTH_KEY_POLL_MS` until it returns a token, calling
   * `done` with it. Stops quietly once `isSettled()` (the caller's own timeout fired).
   */
  private whenAuthKeyIsCached(
    attempt: () => Token | null,
    isSettled: () => boolean,
    done: (token: Token | null) => void
  ): void {
    const first = attempt();
    if (first) {
      done(first);
      return;
    }
    const poll = setInterval(() => {
      if (isSettled()) {
        clearInterval(poll);
        return;
      }
      const token = attempt();
      if (token) {
        clearInterval(poll);
        done(token);
      }
    }, AppService.AUTH_KEY_POLL_MS);
  }

  private teardownAuthObserver(subscription: unknown): void {
    if (typeof subscription === 'function') {
      subscription();
    } else if (subscription && typeof (subscription as Promise<unknown>).then === 'function') {
      (subscription as Promise<unknown>)
        .then((unsub) => {
          if (typeof unsub === 'function') {
            unsub();
          }
        })
        .catch(() => undefined);
    }
  }

  /**
   * Refreshes the cached Daton auth key from the Firebase user's ID token.
   *
   * Always settles: every API call awaits this first, and it used to wait forever when the
   * Firebase session was not ready yet (right after a cross-product handoff), when
   * `getIdToken()` failed, or when the cached `saras_auth_key` was missing, so pages such as
   * the destinations grid showed their loader until a manual refresh. It now resolves `false`
   * after `REFRESH_TOKEN_TIMEOUT_MS`, or as soon as the refresh fails, and the request goes on
   * with the cached token (the server answers 401 if that is stale). When Firebase reports no
   * user it clears the cache and resolves `false` at once, so there is no cached token then;
   * a caller that may run signed out must check for one before `setAuthHeader()`.
   */
  getRefreshToken(): Promise<boolean> {
    return new Promise(resolve => {
      let settled = false;
      let subscription: unknown;
      const settle = (refreshed: boolean) => {
        if (!settled) {
          settled = true;
          clearTimeout(timeout);
          this.teardownAuthObserver(subscription);
          resolve(refreshed);
        }
      };
      const timeout = setTimeout(() => settle(false), AppService.REFRESH_TOKEN_TIMEOUT_MS);

      subscription = this.afAuth.onAuthStateChanged(user => {//console.log(user,"userr")
        if (user) {//console.log(user,"userr")
          user.getIdToken().then(idToken => { //console.log(idToken)

            try {

              // No cached auth key yet means Daton sign-in is still in progress (a handoff from
              // iQ): wait for it instead of failing, bounded by the timeout above.
              this.whenAuthKeyIsCached(() => this.storeRefreshedToken(idToken), () => settled, (token) => settle(!!token));

            } catch (e) {
              settle(false);
            }


          }).catch(() => settle(false));

        } else {

          try {

            this.cacheService.clear();
            localStorage.removeItem('sourceFilter');
            //  this.bootIntercom();
            this.router.navigate([`/${this.getUncommonBaseWord()}/login`]);
            //   window.Intercom('shutdown');
            window['fcWidget'].destroy();

          } catch (e) {

          }
          // Signed out: nothing to refresh, so don't hold the caller for the timeout.
          settle(false);

        }
      });
      if (settled) {
        this.teardownAuthObserver(subscription);
      }
    });

    //  return Promise.resolve(true)


  }

  getRefreshTokenAsObservable(): Observable<any> {
    return new Observable((observer)=>{
      this.afAuth.onAuthStateChanged( user => {//console.log(user,"userr")
        if (user) {//console.log(user,"userr")
          user.getIdToken().then(idToken => { //console.log(idToken)
  
            try {
  
              const token = new Token();
              token.role = this.cacheService.getObject<Token>('saras_auth_key').role;
              token.token = idToken;
              token.userId = this.cacheService.getObject<Token>('saras_auth_key').userId;
              const timestamp = new Date().getTime()
              this.cacheService.putObject('time-laps', timestamp, true);
              this.cacheService.putObject('saras_auth_key', token, true);
  
              observer.next(true)
  
            } catch (e) {
  
            }
  
  
          })
  
        }else{
  
          try{
            this.cacheService.clear();
            localStorage.removeItem('sourceFilter')
            //  this.bootIntercom();
            this.router.navigate(['/' + this.getUncommonBaseWord() + '/login']);
            //   window.Intercom('shutdown');
            window['fcWidget'].destroy();
            
            observer.error({statusText : 'logout'});
          } catch (e) {
  
          }
        }
      })
    })
    

  }

  /**
   * Same refresh as `getRefreshToken`, resolving with the token object. The shared
   * `TokenInterceptor` awaits this for every HTTP request, so a promise that never settled
   * stalled all of them. It now settles with the cached token object (or undefined) when the
   * refresh fails, or after `REFRESH_TOKEN_TIMEOUT_MS`, so requests are sent and fail normally.
   * When Firebase reports no user (signed out) it rejects at once, so the request is not sent
   * without a token.
   */
  returnRefreshToken(): Promise<Token | undefined> {
    return new Promise((resolve, reject) => {
      let settled = false;
      let subscription: unknown;
      const finish = (complete: () => void) => {
        if (!settled) {
          settled = true;
          clearTimeout(timeout);
          this.teardownAuthObserver(subscription);
          complete();
        }
      };
      const settle = (token?: Token) => finish(() => resolve(token));
      const timeout = setTimeout(() => {
        console.warn('[iq-handoff] returnRefreshToken timed out; sending with the cached token');
        settle(this.cacheService.getObject<Token>('saras_auth_key'));
      }, AppService.REFRESH_TOKEN_TIMEOUT_MS);

      subscription = this.afAuth.onAuthStateChanged(user => {//console.log(user,"userr")
        if (user) {//console.log(user,"userr")
          user.getIdToken().then(idToken => { //console.log(idToken)

            try {

              // Same wait as getRefreshToken: the key appears once Daton sign-in finishes.
              this.whenAuthKeyIsCached(
                () => this.storeRefreshedToken(idToken),
                () => settled,
                (token) => settle(token ?? this.cacheService.getObject<Token>('saras_auth_key'))
              );

            } catch (e) {
              console.warn('[iq-handoff] returnRefreshToken could not store the token', e);
              settle(this.cacheService.getObject<Token>('saras_auth_key'));
            }
          }).catch((e) => {
            console.warn('[iq-handoff] returnRefreshToken getIdToken failed', e);
            settle(this.cacheService.getObject<Token>('saras_auth_key'));
          });

        } else {

          try {
            this.cacheService.clear();
            localStorage.removeItem('sourceFilter');
            //  this.bootIntercom();
            this.router.navigate([`/${this.getUncommonBaseWord()}/login`]);
            //   window.Intercom('shutdown');
            window['fcWidget'].destroy();

          } catch (e) {
          }
          finish(() => reject(new Error('Signed out of Firebase; request not sent')));
        }
      });
      if (settled) {
        this.teardownAuthObserver(subscription);
      }
    });
  }

  getServiceStatus() {
    const url = `${this.appUrl}services/status`;
    return this.http.get<any>(url);
  }

  getBrandNamePossibleValues(): Promise<HttpResponse<any>> {
    let url = `${environment.apiUrl}brand/associated-brands/${this.userData.userId}`;

    const headers = this.setAuthHeader();

    return this.http.get<any>(url, {
      headers: headers,
      observe: 'response',
    }).toPromise();
  }
  /**
   * @param leverTypeId - ID indicating the type of lever 
   *  To fetch DATASOURCE data (leverTypeId = 1) 
   *  To fetch DASHBOARD data (leverTypeId = 2)
   *  To fetch FEATURE data (leverTypeId = 3)
   *  To fetch WAREHOUSE data (leverTypeId = 6)
   */
  async getDataSourceNew(leverTypeId: number): Promise<DatasourceResponse> {
    const url = `${environment.subscriptionApiUrl}/subscription-levers/product/1/levers?leverTypeId=${leverTypeId}`;
    const headers = this.setAuthHeader();

    try {
      return await this.http.get<DatasourceResponse>(url, { headers }).toPromise();
    } catch (error) {
      console.error('Error fetching datasource levers:', error);
      throw error;
    }
  }
  async getConnectorDataByType(type: 'datasource' | 'datawarehouse' = 'datasource'): Promise<any[]> {
    const leverTypeId = type === 'datasource' ? 1 : 6;
    // The plan levers only label each connector with its lowest plan (upgrade prompts), so
    // a failure there must not stop the connector list itself from loading. Without them no
    // upgrade prompt shows, so report it rather than drop it silently.
    const [legacyData, newDataResponse] = await Promise.all([
      type === 'datasource' ? this.getDataSources() : this.getDatawarehouses(),
      this.getDataSourceNew(leverTypeId).catch((error): undefined => {
        Sentry.captureException(error, {
          tags: { feature: 'connector-plan-levers', connectorType: type },
        });
        return undefined;
      })
    ]);

    const productLeverList = newDataResponse?.data?.productLeverList || [];

    // Create a map from product name to first subscription plan (since the lowest plan for a source is the first object in the subscriptionPlans array)
    const planMap = new Map<string, any>();
    forEach(productLeverList, product => {
      if (get(product, 'subscriptionPlans.length', 0) > 0) {
        planMap.set(product.masterId, product?.subscriptionPlans?.[0]?.subscriptionPlanGroup);
      }
    });
    
    // Combine legacy data with associated plan
    const combined = map(legacyData, (item: Record<string, any>) => {
      const sourceId = get(item, 'legacySourceId', 1);
      const plan = planMap.get(sourceId);
      return {
        ...item,
        associatedPlan: plan ? this.normalizePlan(plan) : null
      };
    });

    return combined;
  }

  public normalizePlan(plan: string | null | undefined): string | null {
    if (!plan) return null;
    const upperPlan = plan.toUpperCase();
    if (includes(upperPlan, 'GROWTH')) return 'GROWTH';
    if (includes(upperPlan, 'LITE')) return 'LITE';
    if (includes(upperPlan, 'ENTERPRISE')) return 'ENTERPRISE';
    if (includes(upperPlan, 'TRIAL') && includes(upperPlan, 'ENDED')) return 'TRIAL ENDED';
    if (includes(upperPlan, 'TRIAL') && !includes(upperPlan, 'ENDED')) return 'TRIAL';
    return upperPlan;
  }
  private async attachPlanInfoToIntegrations(integrations: any[], type: 'datasource' | 'datawarehouse' = 'datasource'): Promise<any[]> {
    const leverTypeId = type === 'datasource' ? 1 : 6;
    const newDataResponse = await this.getDataSourceNew(leverTypeId);
    const productLeverList = newDataResponse?.data?.productLeverList || [];
    const datasourceId = type === 'datasource' ? 'datasourceId' : 'datawarehouseId';
    const planMap = new Map<number, any>();
    forEach(productLeverList, product => {
      const masterId = product.masterId;
      const planGroup = product.subscriptionPlans?.[0]?.subscriptionPlanGroup;
      if (masterId && planGroup) {
        planMap.set(masterId, this.normalizePlan(planGroup));
      }
    });
    console.log('Masterid and corresponding plan',planMap);
    return integrations.map(integration => ({
      ...integration,
      associatedPlan: planMap.get(integration?.[datasourceId]) || null
    }));
  }
  private getPlanLevel(plan: string): number {
    const planLevels = {
      'TRIAL': 0,
      'LITE': 1,
      'GROWTH': 2,
      'ENTERPRISE': 3
    };
    return planLevels[plan] || 0;
  }

  public shouldShowUpgradeDialog(userPlan: string, connectorPlan: string): boolean {
    if (!userPlan || !connectorPlan) return false;
    if (userPlan === 'ENTERPRISE' || userPlan === 'TRIAL') return false;
    return this.getPlanLevel(userPlan) < this.getPlanLevel(connectorPlan);
  }

  public getUpgradeDialogTitle(source: string, userPlan: string, connectorPlan: string): string {
   if (userPlan === 'LITE') {
      if (connectorPlan === 'GROWTH') {
        return `${source} is available for Growth & Enterprise users`;
      } else if (connectorPlan === 'ENTERPRISE') {
        return `${source} is available for Enterprise users`;
      }
    } else if (userPlan === 'GROWTH') {
      if (connectorPlan === 'ENTERPRISE') {
        return `${source} is available for Enterprise users`;
      }
    }
    return `${source} is available for ${connectorPlan} users`;
  }
}